import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Divider,
  Radio,
  Tooltip,
  Badge,
  Typography,
  Modal,
  Alert,
  message,
  Form,
  InputNumber,
  Checkbox,
  Spin,
  Switch, Empty,  
} from 'antd';
import {
  CalculatorOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  NumberOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
  EyeOutlined,
  FunctionOutlined,
  SettingOutlined,
  DatabaseOutlined,
  TagOutlined,
  LockOutlined,
  ShareAltOutlined,
  UserOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  EyeInvisibleOutlined,
  FontSizeOutlined,
  CodeOutlined,
  FontColorsOutlined,
  ColumnWidthOutlined,
  FieldTimeOutlined,
  KeyOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

// Step Combine Operators
const STEP_COMBINE_OPERATORS = [
  { value: 'none', label: 'None (Show step results only)', symbol: '||' },
  { value: 'add', label: '+ Addition', symbol: '+' },
  { value: 'subtract', label: '- Subtraction', symbol: '-' },
  { value: 'multiply', label: '× Multiplication', symbol: '×' },
  { value: 'divide', label: '÷ Division', symbol: '÷' },
  { value: 'percentage', label: '% Percentage of', symbol: '%' },
  { value: 'equals', label: '== Equals', symbol: '==' },
  { value: 'not_equals', label: '≠ Not Equals', symbol: '≠' },
  { value: 'greater_than', label: '> Greater Than', symbol: '>' },
  { value: 'less_than', label: '< Less Than', symbol: '<' },
  { value: 'greater_than_equal', label: '≥ Greater or Equal', symbol: '≥' },
  { value: 'less_than_equal', label: '≤ Less or Equal', symbol: '≤' },
];

// Generate unique ID
const uid = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;

// Helper function to check if operator is logical
const isLogicalOperator = (operator) => {
  const logicalOps = ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_equal', 'less_than_equal'];
  return logicalOps.includes(operator);
};

// ==================== NUMBER TO WORDS CONVERSION ====================
const numberToWords = (num) => {
  if (num === '' || num === null || num === undefined) return '';
  
  const numStr = String(num).replace(/,/g, '');
  if (isNaN(numStr) || numStr === '') return '';
  
  const number = parseFloat(numStr);
  if (isNaN(number)) return '';
  
  const parts = numStr.split('.');
  const integerPart = parseInt(parts[0], 10);
  const decimalPart = parts[1] ? parseInt(parts[1], 10) : null;
  
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n / 10)] + ' ' + (n % 10 !== 0 ? ones[n % 10] + ' ' : '');
    return ones[Math.floor(n / 100)] + ' hundred ' + convertLessThanThousand(n % 100);
  };
  
  const convertToWords = (n) => {
    if (n === 0) return 'zero';
    const units = ['', 'thousand', 'million', 'billion', 'trillion', 'quadrillion'];
    let result = '';
    let unitIndex = 0;
    let tempN = n;
    while (tempN > 0) {
      const chunk = tempN % 1000;
      if (chunk !== 0) {
        result = convertLessThanThousand(chunk) + units[unitIndex] + ' ' + result;
      }
      tempN = Math.floor(tempN / 1000);
      unitIndex++;
    }
    return result.trim();
  };
  
  let words = convertToWords(integerPart);
  
  if (decimalPart !== null && decimalPart > 0) {
    const decimalWords = convertToWords(decimalPart);
    words += ` point ${decimalWords}`;
  }
  
  return words;
};

// Helper function to get words representation for display
const getWordsRepresentation = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '';
  if (numValue === 0) return '';
  const words = numberToWords(numValue);
  if (words && words !== 'zero') {
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return '';
};

// ==================== NUMBER VALIDATION FUNCTION ====================
const applyNumberValidation = (field, value) => {
  const validation = field.validation || {};
  let transformed = value || '';
  let error = null;
  const exactLength = validation.exactLength;
  const isIntegerOnly = validation.integerOnly === true;
  const allowDecimals = validation.allowDecimals !== false && !isIntegerOnly;

  if (!value) return { transformed: value, error: null };

  const originalValue = value;
  let hasLeadingZeros = false;
  
  if (typeof originalValue === 'string' && originalValue.length > 1) {
    hasLeadingZeros = originalValue[0] === '0' && originalValue[1] !== '.' && originalValue[1] !== '-';
  }
  const isAllZeros = /^0+$/.test(originalValue);

  // Remove spaces if not allowed
  if (validation.allowSpaces === false) transformed = transformed.replace(/\s/g, '');
  if (validation.allowLeadingSpaces === false && transformed.startsWith(' ')) transformed = transformed.trimStart();
  if (validation.allowTrailingSpaces === false && transformed.endsWith(' ')) transformed = transformed.trimEnd();

  // CRITICAL FIX: If integer only, remove decimal point completely
  if (isIntegerOnly) {
    transformed = transformed.replace(/\./g, '');
    transformed = transformed.replace(/[.]/g, '');
  }

  // Filter characters based on allowed types
  if (validation.allowNegative && allowDecimals) {
    transformed = transformed.replace(/[^-0-9.]/g, '');
  } else if (validation.allowNegative && !allowDecimals) {
    transformed = transformed.replace(/[^-0-9]/g, '');
  } else if (!validation.allowNegative && allowDecimals) {
    transformed = transformed.replace(/[^0-9.]/g, '');
  } else {
    transformed = transformed.replace(/[^0-9]/g, '');
  }

  // Handle decimals - only if decimals are allowed
  if (!allowDecimals) {
    transformed = transformed.replace(/\./g, '');
  }
  
  const decimalCount = (transformed.match(/\./g) || []).length;
  if (decimalCount > 1) {
    const lastIndex = transformed.lastIndexOf('.');
    transformed = transformed.substring(0, lastIndex) + transformed.substring(lastIndex + 1).replace(/\./g, '');
  }

  // Handle negative sign
  if (!validation.allowNegative && transformed.includes('-')) {
    error = "✗ Negative numbers are not allowed";
    transformed = transformed.replace(/-/g, '');
  } else if (validation.allowNegative && transformed.includes('-')) {
    if (transformed.indexOf('-') !== 0) transformed = '-' + transformed.replace(/-/g, '');
    const negativeCount = (transformed.match(/-/g) || []).length;
    if (negativeCount > 1) transformed = '-' + transformed.replace(/-/g, '');
  }

  // Apply exact length constraint (only on digits, ignore decimal point for integer only)
  if (exactLength && transformed && !error) {
    let cleanedForLength;
    if (isIntegerOnly) {
      cleanedForLength = transformed.replace(/[^0-9]/g, '');
    } else {
      cleanedForLength = transformed.replace(/[^0-9]/g, '');
    }
    
    if (cleanedForLength.length > exactLength) {
      const allowedDigits = cleanedForLength.slice(0, exactLength);
      const hasNegative = transformed.startsWith('-');
      
      if (!isIntegerOnly && allowDecimals && transformed.includes('.')) {
        const decimalPart = transformed.slice(transformed.indexOf('.') + 1);
        const limitedDecimal = decimalPart.slice(0, validation.decimalPlaces || decimalPart.length);
        transformed = (hasNegative ? '-' : '') + allowedDigits + '.' + limitedDecimal;
      } else {
        transformed = (hasNegative ? '-' : '') + allowedDigits;
      }
    }
  }

  // Parse number and apply additional validations
  const numValue = parseFloat(transformed);
  
  if (!isNaN(numValue) && transformed !== '') {
    if (validation.allowZero === false && numValue === 0) {
      error = "✗ Zero is not allowed";
      transformed = '';
    }
    if (isIntegerOnly && transformed.includes('.')) {
      error = "✗ Integer only - decimals not allowed";
      transformed = transformed.split('.')[0];
    }
    if (!allowDecimals && transformed.includes('.')) {
      error = "✗ Decimals are not allowed";
      transformed = transformed.split('.')[0];
    }
    if (validation.decimalPlaces && validation.decimalPlaces > 0 && allowDecimals && transformed.includes('.')) {
      const parts = transformed.split('.');
      if (parts[1] && parts[1].length > validation.decimalPlaces) {
        const rounded = numValue.toFixed(validation.decimalPlaces);
        transformed = rounded;
      }
    }
  }

  // Preserve leading zeros if needed
  if ((hasLeadingZeros || isAllZeros) && !error && transformed && !transformed.includes('.')) {
    const originalNumericPart = originalValue.replace(/[^0-9]/g, '');
    const currentNumericPart = transformed.replace(/[^0-9]/g, '');
    
    if (originalNumericPart.length === currentNumericPart.length) {
      const preservedZeros = originalNumericPart;
      const hasNegative = transformed.startsWith('-');
      transformed = (hasNegative ? '-' : '') + preservedZeros;
    }
  }

  return { transformed, error };
};

// ==================== VALIDATION PANEL COMPONENT ====================
const ValidationPanel = ({ field, onUpdate, language }) => {
  const [activeTab, setActiveTab] = useState('numbers');

  const updateValidation = (key, value) => {
    const updatedField = { ...field };
    if (!updatedField.validation) {
      updatedField.validation = {};
    }
    updatedField.validation[key] = value;
    onUpdate(updatedField);
  };

  const tabs = [
    { key: 'length', label: language === 'english' ? 'Length' : 'लांबी', icon: <ColumnWidthOutlined /> },
    { key: 'numbers', label: language === 'english' ? 'Numbers' : 'संख्या', icon: <NumberOutlined /> },
    { key: 'spaces', label: language === 'english' ? 'Spaces' : 'स्पेस', icon: <FontColorsOutlined /> },
  ];

  return (
    <Card
      size="small"
      style={{
        marginTop: 16,
        background: "#fafafa",
        border: "1px solid #d9d9d9",
        borderRadius: "8px"
      }}
      title={
        <Space>
          <LockOutlined style={{ color: "#722ed1" }} />
          <Text strong>
            {language === 'english' ? 'Validation Rules' : 'वैधता नियम'}
          </Text>
        </Space>
      }
      extra={
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <Button
              key={tab.key}
              type={activeTab === tab.key ? 'primary' : 'text'}
              size="small"
              icon={tab.icon}
              onClick={() => setActiveTab(tab.key)}
              style={{ 
                padding: '4px 8px',
                background: activeTab === tab.key ? '#722ed1' : 'transparent',
                color: activeTab === tab.key ? 'white' : '#666',
                border: activeTab === tab.key ? 'none' : '1px solid #d9d9d9'
              }}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      }
    >
      {/* LENGTH TAB */}
      {activeTab === 'length' && (
        <div>
          <Row gutter={16} style={{ marginBottom: 8 }}>
            <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}>
              <Text type="secondary">{language === 'english' ? 'Exact Length' : 'निश्चित लांबी'}</Text>
            </Col>
            <Col span={16}>
              <Input
                type="number"
                min={1}
                value={field.validation?.exactLength || ''}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null;
                  updateValidation("exactLength", val);
                }}
                placeholder="Enter exact length"
                style={{ width: 120 }}
                size="small"
              />
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                {language === 'english' ? 'digits' : 'अंक'}
              </Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={8}></Col>
            <Col span={16}>
              <Alert
                message={language === 'english' 
                  ? "Field must be exactly this many digits long" 
                  : "फील्ड या अचूक संख्येच्या अंकांचे असणे आवश्यक आहे"}
                type="info"
                showIcon
                style={{ fontSize: '12px' }}
              />
            </Col>
          </Row>
        </div>
      )}

      {/* NUMBERS TAB */}
      {activeTab === 'numbers' && (
        <div>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}>
              <Text type="secondary">{language === 'english' ? 'Decimal Places' : 'दशांश स्थळे'}</Text>
            </Col>
            <Col span={16}>
              <Input
                type="number"
                min={0}
                max={10}
                value={field.validation?.decimalPlaces || ''}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null;
                  updateValidation("decimalPlaces", val);
                  if (val && val > 0) {
                    updateValidation("allowDecimals", true);
                  }
                }}
                placeholder="No restriction"
                size="small"
                style={{ width: 120 }}
              />
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                {language === 'english' ? 'digits after decimal' : 'दशांशानंतरचे अंक'}
              </Text>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 8 }}>
            <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}>
              <Text type="secondary">{language === 'english' ? 'Number Options' : 'संख्या पर्याय'}</Text>
            </Col>
            <Col span={16}>
              <Space direction="vertical" size={4}>
                <Checkbox
                  checked={field.validation?.allowZero !== false}
                  onChange={(e) => updateValidation("allowZero", e.target.checked)}
                >
                  {language === 'english' ? 'Allow zero' : 'शून्याला परवानगी द्या'}
                </Checkbox>
                <Checkbox
                  checked={field.validation?.allowDecimals || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateValidation("allowDecimals", checked);
                    if (!checked) {
                      updateValidation("decimalPlaces", null);
                    }
                  }}
                >
                  {language === 'english' ? 'Allow decimals' : 'दशांशांना परवानगी द्या'}
                </Checkbox>
                <Checkbox
                  checked={field.validation?.allowNegative || false}
                  onChange={(e) => updateValidation("allowNegative", e.target.checked)}
                >
                  {language === 'english' ? 'Allow negative numbers' : 'ऋण संख्यांना परवानगी द्या'}
                </Checkbox>
                <Checkbox
                  checked={field.validation?.integerOnly || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateValidation("integerOnly", checked);
                    if (checked) {
                      updateValidation("allowDecimals", false);
                      updateValidation("decimalPlaces", 0);
                    }
                  }}
                >
                  {language === 'english' ? 'Integer only (no decimals)' : 'फक्त पूर्णांक (दशांश नको)'}
                </Checkbox>
              </Space>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={8}></Col>
            <Col span={16}>
              <Alert
                message={language === 'english' 
                  ? "Default: Zero allowed, no decimals, no negatives" 
                  : "डीफॉल्ट: शून्य अनुमत, दशांश नाही, ऋण नाही"}
                type="info"
                showIcon
                style={{ fontSize: '12px' }}
              />
            </Col>
          </Row>
        </div>
      )}

      {/* SPACES TAB */}
      {activeTab === 'spaces' && (
        <div>
          <Row gutter={16} style={{ marginBottom: 8 }}>
            <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}>
              <Text type="secondary">{language === 'english' ? 'Space Rules' : 'स्पेस नियम'}</Text>
            </Col>
            <Col span={16}>
              <Space direction="vertical" size={4}>
                <Checkbox
                  checked={field.validation?.allowSpaces !== false}
                  onChange={(e) => updateValidation("allowSpaces", e.target.checked)}
                >
                  {language === 'english' ? 'Allow spaces' : 'स्पेसला परवानगी द्या'}
                </Checkbox>
                <Checkbox
                  checked={field.validation?.trimInput !== false}
                  onChange={(e) => updateValidation("trimInput", e.target.checked)}
                >
                  {language === 'english' ? 'Automatically trim spaces' : 'आपोआप स्पेस काढा'}
                </Checkbox>
              </Space>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
};

// ==================== SIMPLE UNIT CHECKBOX COMPONENT ====================
const SimpleUnitCheckboxForFormula = ({ field, onUpdate, language }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(field.selectedUnit || null);
  const [addUnitModalVisible, setAddUnitModalVisible] = useState(false);
  const [unitSelectionModalVisible, setUnitSelectionModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [tempSelectedUnit, setTempSelectedUnit] = useState(null);

  const loadUnits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/forms/units?active_only=true`);
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setUnits(response.data.data);
      } else if (Array.isArray(response.data)) {
        setUnits(response.data);
      } else {
        setUnits([]);
      }
    } catch (error) {
      console.error('Failed to load units:', error);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  const openUnitSelectionModal = async () => {
    setTempSelectedUnit(selectedUnit);
    await loadUnits();
    setUnitSelectionModalVisible(true);
  };

  const handleUnitToggle = (checked) => {
    if (checked) {
      openUnitSelectionModal();
    } else {
      const updatedField = {
        ...field,
        hasUnit: false,
        selectedUnit: null,
        selectedUnitObject: null,
        units: { enabled: false }
      };
      setSelectedUnit(null);
      onUpdate(updatedField);
    }
  };

  const handleConfirmUnitSelection = () => {
    if (!tempSelectedUnit) {
      message.warning(language === "english" ? "Please select a unit" : "कृपया एक युनिट निवडा");
      return;
    }

    const selectedUnitObj = units.find(u => u.id === tempSelectedUnit);
    setSelectedUnit(tempSelectedUnit);

    const updatedField = {
      ...field,
      hasUnit: true,
      selectedUnit: tempSelectedUnit,
      selectedUnitObject: selectedUnitObj,
      units: {
        enabled: true,
        selectedUnit: selectedUnitObj?.unit_name,
        selectedUnitId: tempSelectedUnit,
        selectedUnitValue: selectedUnitObj?.unit_value,
        selectedUnitSymbol: selectedUnitObj?.unit_symbol,
        selectedUnitLabel: selectedUnitObj?.unit_name,
        unitCategory: selectedUnitObj?.unit_category,
      }
    };

    onUpdate(updatedField);
    setUnitSelectionModalVisible(false);
  };

  const handleUnitSelectInModal = (unitId) => {
    setTempSelectedUnit(unitId);
  };

  const handleAddUnit = async (values) => {
    try {
      setSubmitting(true);
      const response = await axios.post(`${API_BASE}/forms/units`, values);
      if (response.data && response.data.success) {
        await loadUnits();
        const newUnitId = response.data.data?.id;
        if (newUnitId) setTempSelectedUnit(newUnitId);
        setAddUnitModalVisible(false);
        form.resetFields();
      } else {
        throw new Error(response.data?.error || 'Failed to create unit');
      }
    } catch (error) {
      message.error(language === "english" ? "Failed to create unit" : "युनिट तयार करण्यात अयशस्वी");
    } finally {
      setSubmitting(false);
    }
  };

  const groupedUnits = useMemo(() => {
    const groups = {};
    units.forEach(unit => {
      const category = unit.unit_category || 'Other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(unit);
    });
    return groups;
  }, [units]);

  return (
    <div style={{ marginTop: 8, marginBottom: 16, padding: "12px", background: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: "6px" }}>
      <Row align="middle" gutter={[16, 8]}>
        <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>{language === "english" ? "Unit Option" : "युनिट पर्याय"}</Text>
        </Col>
        <Col span={16}>
          <Checkbox checked={field.hasUnit || false} onChange={(e) => handleUnitToggle(e.target.checked)}>
            <Space size={4}>
              <span style={{ fontSize: "12px" }}>{language === "english" ? "Enable unit selection" : "युनिट निवड सक्षम करा"}</span>
            </Space>
          </Checkbox>
          {field.hasUnit && selectedUnit && (
            <div style={{ marginTop: 8 }}>
              <div style={{ padding: "6px 10px", background: "#e6f7ff", borderRadius: "4px", border: "1px solid #91d5ff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Space size={4}>
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "12px" }} />
                  <Text type="secondary" style={{ fontSize: "11px" }}>{language === "english" ? "Selected unit:" : "निवडलेले युनिट:"}</Text>
                  <Text strong style={{ fontSize: "12px" }}>{units.find(u => u.id === selectedUnit)?.unit_name}</Text>
                  {units.find(u => u.id === selectedUnit)?.unit_symbol && <Tag color="blue" style={{ fontSize: "10px", margin: 0 }}>{units.find(u => u.id === selectedUnit)?.unit_symbol}</Tag>}
                </Space>
                <Button type="link" size="small" onClick={openUnitSelectionModal}>{language === "english" ? "Change" : "बदला"}</Button>
              </div>
            </div>
          )}
        </Col>
      </Row>

      <Modal
        open={unitSelectionModalVisible}
        onCancel={() => { setUnitSelectionModalVisible(false); setTempSelectedUnit(selectedUnit); }}
        title={<span>{language === "english" ? "Select Unit" : "युनिट निवडा"}</span>}
        footer={[
          <Button key="cancel" onClick={() => { setUnitSelectionModalVisible(false); setTempSelectedUnit(selectedUnit); }}>{language === "english" ? "Cancel" : "रद्द करा"}</Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmUnitSelection} disabled={!tempSelectedUnit}>{language === "english" ? "Confirm" : "पुष्टी करा"}</Button>
        ]}
        width={500}
        centered
        destroyOnClose
      >
        {loading ? <div style={{ textAlign: "center", padding: "30px" }}><Spin /></div> : units.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px" }}>
            <DatabaseOutlined style={{ fontSize: 36, color: "#999", marginBottom: 12 }} />
            <div>{language === "english" ? "No units found. Create a new unit." : "कोणतेही युनिट सापडले नाही."}</div>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setUnitSelectionModalVisible(false); setAddUnitModalVisible(true); }} style={{ marginTop: 12 }}>
              {language === "english" ? "Create New Unit" : "नवीन युनिट तयार करा"}
            </Button>
          </div>
        ) : (
          <div style={{ maxHeight: "350px", overflowY: "auto" }}>
            <Radio.Group value={tempSelectedUnit} onChange={(e) => handleUnitSelectInModal(e.target.value)} style={{ width: "100%" }}>
              {Object.keys(groupedUnits).map(category => (
                <div key={category} style={{ marginBottom: 16 }}>
                  <div style={{ padding: "6px 10px", background: "#f0f0f0", borderRadius: "4px", marginBottom: 6 }}>
                    <strong>{category}</strong> <Tag color="blue">{groupedUnits[category].length}</Tag>
                  </div>
                  <div style={{ paddingLeft: 8 }}>
                    {groupedUnits[category].map(unit => (
                      <div
                        key={unit.id}
                        style={{
                          padding: "6px 10px",
                          marginBottom: "6px",
                          background: tempSelectedUnit === unit.id ? "#e6f7ff" : "#fafafa",
                          borderRadius: "4px",
                          border: tempSelectedUnit === unit.id ? "1px solid #1890ff" : "1px solid #e8e8e8",
                          cursor: "pointer"
                        }}
                        onClick={() => handleUnitSelectInModal(unit.id)}
                      >
                        <Radio value={unit.id}>
                          <strong>{unit.unit_name}</strong>
                          {unit.unit_symbol && <Tag color="blue" style={{ marginLeft: 8 }}>{unit.unit_symbol}</Tag>}
                        </Radio>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Radio.Group>
          </div>
        )}
        <Divider />
        <Button type="dashed" icon={<PlusOutlined />} block onClick={() => { setUnitSelectionModalVisible(false); setAddUnitModalVisible(true); }}>
          {language === "english" ? "+ Create New Unit" : "+ नवीन युनिट तयार करा"}
        </Button>
      </Modal>

      <Modal
        open={addUnitModalVisible}
        onCancel={() => { setAddUnitModalVisible(false); form.resetFields(); }}
        title={language === "english" ? "Create New Unit" : "नवीन युनिट तयार करा"}
        footer={null}
        centered
        destroyOnClose
        width={450}
      >
        <Form form={form} layout="vertical" size="small" onFinish={handleAddUnit}>
          <Form.Item name="unit_name" label={language === "english" ? "Unit Name *" : "युनिटचे नाव *"} rules={[{ required: true }]}>
            <Input placeholder={language === "english" ? "e.g., Kilogram" : "उदा., किलोग्रॅम"} />
          </Form.Item>
          <Form.Item name="unit_value" label={language === "english" ? "Unit Value *" : "युनिट मूल्य *"} rules={[{ required: true }]} extra={language === "english" ? "Base conversion factor" : "मूलभूत रूपांतरण घटक"}>
            <Input placeholder="1 / 0.001 / 1000" />
          </Form.Item>
          <Form.Item name="unit_symbol" label={language === "english" ? "Unit Symbol" : "युनिट चिन्ह"}>
            <Input placeholder={language === "english" ? "e.g., kg" : "उदा., किग्रॅ"} />
          </Form.Item>
          <Form.Item name="unit_category" label={language === "english" ? "Category" : "श्रेणी"}>
            <Select placeholder={language === "english" ? "Select category" : "श्रेणी निवडा"} allowClear>
              <Option value="weight">Weight / वजन</Option>
              <Option value="length">Length / लांबी</Option>
              <Option value="volume">Volume / घनफळ</Option>
              <Option value="area">Area / क्षेत्रफळ</Option>
              <Option value="time">Time / वेळ</Option>
              <Option value="speed">Speed / वेग</Option>
              <Option value="temperature">Temperature / तापमान</Option>
              <Option value="digital">Digital / डिजिटल</Option>
              <Option value="currency">Currency / चलन</Option>
              <Option value="number_system">Number System / संख्या प्रणाली</Option>
              <Option value="percentage">Percentage / टक्केवारी</Option>
            </Select>
          </Form.Item>
          <Form.Item name="is_active" valuePropName="checked" initialValue={true}>
            <Checkbox>{language === "english" ? "Active" : "सक्रिय"}</Checkbox>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => { setAddUnitModalVisible(false); form.resetFields(); }}>{language === "english" ? "Cancel" : "रद्द करा"}</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>{language === "english" ? "Create" : "तयार करा"}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ==================== MODAL FOR ADDING FIELD TO ANOTHER STEP ====================
const AddFieldToStepModal = ({
  visible,
  onClose,
  onConfirm,
  fieldData,
  sourceStepId,
  sourceFieldId,
  steps,
  language
}) => {
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState('add');

  useEffect(() => {
    if (visible) {
      setSelectedStepId(null);
      setSelectedOperator('add');
    }
  }, [visible]);

  const availableSteps = steps.filter(step => step.id !== sourceStepId);

  const handleConfirm = () => {
    if (!selectedStepId) {
      message.warning(language === "english" ? "Please select a step" : "कृपया एक पायरी निवडा");
      return;
    }
    onConfirm({
      targetStepId: selectedStepId,
      sourceFieldId: sourceFieldId,
      fieldData: fieldData,
      operator: selectedOperator
    });
    onClose();
  };

  const getOperatorSymbol = (op) => {
    switch (op) {
      case 'add': return '+';
      case 'subtract': return '-';
      case 'multiply': return '×';
      case 'divide': return '÷';
      default: return '+';
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <Space>
          <ShareAltOutlined style={{ color: "#1890ff" }} />
          <span>{language === "english" ? "Add Field to Another Step" : "दुसऱ्या पायरीमध्ये फील्ड जोडा"}</span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose}>{language === "english" ? "Cancel" : "रद्द करा"}</Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm} disabled={!selectedStepId}>
          {language === "english" ? "Add to Step" : "पायरीमध्ये जोडा"}
        </Button>
      ]}
      width={500}
      centered
      destroyOnClose
    >
      <div style={{ padding: "12px", background: "#f0f5ff", borderRadius: "8px", marginBottom: "16px" }}>
        <Text type="secondary">{language === "english" ? "Field to share:" : "शेअर करायचे फील्ड:"}</Text>
        <div style={{ marginTop: "8px" }} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text strong>{language === "english" ? "Select Target Step:" : "लक्ष्य पायरी निवडा:"}</Text>
        {availableSteps.length === 0 ? (
          <Alert message={language === "english" ? "No other steps available" : "इतर कोणतीही पायरी उपलब्ध नाही"} type="info" showIcon />
        ) : (
          <Radio.Group value={selectedStepId} onChange={(e) => setSelectedStepId(e.target.value)} style={{ width: "100%", marginTop: 8 }}>
            {availableSteps.map(step => (
              <div key={step.id} style={{ padding: 8, border: selectedStepId === step.id ? "1px solid #1890ff" : "1px solid #d9d9d9", borderRadius: 6, marginBottom: 8 }}>
                <Radio value={step.id}>
                  <strong>{step.name}</strong> ({step.arithmeticFields?.length || 0} fields)
                </Radio>
              </div>
            ))}
          </Radio.Group>
        )}
      </div>

      <div>
        <Text strong>{language === "english" ? "Operator:" : "ऑपरेटर:"}</Text>
        <Select value={selectedOperator} onChange={setSelectedOperator} style={{ width: "100%", marginTop: 8 }}>
          <Option value="add">+ ({language === "english" ? "Addition" : "बेरीज"})</Option>
          <Option value="subtract">- ({language === "english" ? "Subtraction" : "वजाबाकी"})</Option>
          <Option value="multiply">× ({language === "english" ? "Multiplication" : "गुणाकार"})</Option>
          <Option value="divide">÷ ({language === "english" ? "Division" : "भागाकार"})</Option>
        </Select>
      </div>
    </Modal>
  );
};

// ==================== MODAL FOR SHARING STEP RESULT ====================
const ShareStepResultModal = ({
  visible,
  onClose,
  onConfirm,
  sourceStep,
  sourceStepId,
  steps,
  language
}) => {
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState('add');

  useEffect(() => {
    if (visible) {
      setSelectedStepId(null);
      setSelectedOperator('add');
    }
  }, [visible]);

  // Filter out the source step
  const availableSteps = steps.filter(step => step.id !== sourceStepId);

  const handleConfirm = () => {
    if (!selectedStepId) {
      message.warning(language === "english" ? "Please select a step" : "कृपया एक पायरी निवडा");
      return;
    }
    onConfirm({
      targetStepId: selectedStepId,
      sourceStepId: sourceStepId,
      sourceStepName: sourceStep?.name || `Step ${steps.findIndex(s => s.id === sourceStepId) + 1}`,
      stepResultVariable: sourceStep?.resultVariable,
      stepLabel: sourceStep?.resultLabel,
      operator: selectedOperator
    });
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <Space>
          <ShareAltOutlined style={{ color: "#722ed1" }} />
          <span>{language === "english" ? "Share Step Result to Another Step" : "दुसऱ्या पायरीमध्ये पायरीचा निकाल शेअर करा"}</span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose}>{language === "english" ? "Cancel" : "रद्द करा"}</Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm} disabled={!selectedStepId}>
          {language === "english" ? "Add Result to Step" : "पायरीमध्ये निकाल जोडा"}
        </Button>
      ]}
      width={500}
      centered
      destroyOnClose
    >
      <div style={{ padding: "12px", background: "#f0f5ff", borderRadius: "8px", marginBottom: "16px" }}>
        <Text type="secondary">{language === "english" ? "Step result to share:" : "शेअर करायचा पायरीचा निकाल:"}</Text>
        <div style={{ marginTop: "8px", padding: "8px", background: "#e6f7ff", borderRadius: "4px", border: "1px solid #91d5ff" }}>
          <Text strong>{sourceStep?.name || `Step ${steps.findIndex(s => s.id === sourceStepId) + 1}`}</Text>
          <Tag color="blue" style={{ marginLeft: "8px" }}>Result: {(sourceStep?.resultVariable || '')}</Tag>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text strong>{language === "english" ? "Select Target Step:" : "लक्ष्य पायरी निवडा:"}</Text>
        {availableSteps.length === 0 ? (
          <Alert message={language === "english" ? "No other steps available" : "इतर कोणतीही पायरी उपलब्ध नाही"} type="info" showIcon />
        ) : (
          <Radio.Group value={selectedStepId} onChange={(e) => setSelectedStepId(e.target.value)} style={{ width: "100%", marginTop: 8 }}>
            {availableSteps.map(step => (
              <div key={step.id} style={{ padding: 8, border: selectedStepId === step.id ? "1px solid #1890ff" : "1px solid #d9d9d9", borderRadius: 6, marginBottom: 8 }}>
                <Radio value={step.id}>
                  <strong>{step.name}</strong> ({step.arithmeticFields?.length || 0} fields)
                </Radio>
              </div>
            ))}
          </Radio.Group>
        )}
      </div>

      <div>
        <Text strong>{language === "english" ? "Operator:" : "ऑपरेटर:"}</Text>
        <Select value={selectedOperator} onChange={setSelectedOperator} style={{ width: "100%", marginTop: 8 }}>
          <Option value="add">+ ({language === "english" ? "Addition" : "बेरीज"})</Option>
          <Option value="subtract">- ({language === "english" ? "Subtraction" : "वजाबाकी"})</Option>
          <Option value="multiply">× ({language === "english" ? "Multiplication" : "गुणाकार"})</Option>
          <Option value="divide">÷ ({language === "english" ? "Division" : "भागाकार"})</Option>
          <Option value="percentage">% ({language === "english" ? "Percentage" : "टक्केवारी"})</Option>
        </Select>
      </div>

      <Divider />
      <div style={{ padding: "8px", background: "#fffbe6", borderRadius: "6px", border: "1px solid #ffe58f" }}>
        <Text type="secondary" style={{ fontSize: "11px" }}>
          <InfoCircleOutlined style={{ marginRight: "4px", color: "#faad14" }} />
          {language === "english"
            ? "The step result will be added as an auto-calculated value. This creates a dependency between steps."
            : "पायरीचा निकाल एक स्वयं-गणना मूल्य म्हणून जोडला जाईल. यामुळे पायऱ्यांमध्ये अवलंबित्व निर्माण होते."}
        </Text>
      </div>
    </Modal>
  );
};

// ==================== MAIN COMPONENT ====================
const MultiLevelFormulaFieldEditor = ({
  field,
  onUpdate,
  onDelete,
  onCopy,
  onFieldValueChange,
  allFields,
  language,
  autoTranslate,
  disabled,
  previewMode = false
}) => {
  const [activeLanguage, setActiveLanguage] = useState(language || 'english');
  const [showFieldConfigModal, setShowFieldConfigModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingStepId, setEditingStepId] = useState(null);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [stepResults, setStepResults] = useState({});
  const [fieldValues, setFieldValues] = useState({});
  const [previewErrors, setPreviewErrors] = useState({});

  // State for Add to Step Modal
  const [addToStepModalVisible, setAddToStepModalVisible] = useState(false);
  const [selectedFieldForSharing, setSelectedFieldForSharing] = useState(null);
  const [selectedSourceStepId, setSelectedSourceStepId] = useState(null);
  const [selectedSourceFieldIndex, setSelectedSourceFieldIndex] = useState(null);

  // State for Share Step Result Modal
  const [shareStepResultModalVisible, setShareStepResultModalVisible] = useState(false);
  const [selectedSourceStepForSharing, setSelectedSourceStepForSharing] = useState(null);

  // State for External Fields Modal
  const [showExternalFieldsModal, setShowExternalFieldsModal] = useState(false);
  const [currentStepIdForExternal, setCurrentStepIdForExternal] = useState(null);
  const [currentStepFields, setCurrentStepFields] = useState([]);

  // State for False Message Modal
  const [falseMessageModalVisible, setFalseMessageModalVisible] = useState(false);
  const [currentFalseMessageStepId, setCurrentFalseMessageStepId] = useState(null);
  const [currentFalseMessageType, setCurrentFalseMessageType] = useState(null);
  const [currentArithmeticFieldId, setCurrentArithmeticFieldId] = useState(null);

  // Show/Hide Result Checkbox State
  const [showResult, setShowResult] = useState(field.showResult !== false);

  // Final Result Label State
  const [finalResultLabel, setFinalResultLabel] = useState({
    en: field.finalResultLabel?.en || 'Final Result',
    mr: field.finalResultLabel?.mr || 'अंतिम निकाल'
  });

  // Final Result False Message State
  const [finalFalseMessage, setFinalFalseMessage] = useState(field.finalFalseMessage || '');

  const updateField = useCallback((key, value) => {
    if (disabled) return;
    onUpdate({ ...field, [key]: value });
  }, [disabled, field, onUpdate]);

  const updateShowResult = useCallback((checked) => {
    setShowResult(checked);
    updateField('showResult', checked);
  }, [updateField]);

  const updateFinalResultLabel = useCallback((lang, value) => {
    const newLabel = { ...finalResultLabel, [lang === 'english' ? 'en' : 'mr']: value };
    setFinalResultLabel(newLabel);
    updateField('finalResultLabel', newLabel);
  }, [finalResultLabel, updateField]);

  const updateFinalFalseMessage = useCallback((message) => {
    setFinalFalseMessage(message);
    updateField('finalFalseMessage', message);
  }, [updateField]);

  // Initialize formula steps
  useEffect(() => {
    if (!field.formulaSteps || field.formulaSteps.length === 0) {
      const defaultSteps = [{
        id: Date.now(),
        name: 'Step 1',
        arithmeticFields: [],
        combineOperator: 'add',
        resultVariable: 'step_1_result',
        resultLabel: { en: 'Step 1 Result', mr: 'पायरी 1 चे निकाल' },
        showStepResult: true,
        falseMessage: ''
      }];
      updateField('formulaSteps', defaultSteps);
    }
    if (!field.stepCombineOperator) updateField('stepCombineOperator', 'add');
    if (field.required === undefined) updateField('required', false);
    if (field.showResult === undefined) updateField('showResult', true);
    if (field.finalFalseMessage === undefined) updateField('finalFalseMessage', '');
  }, []);

  // Initialize field values
  useEffect(() => {
    const steps = field.formulaSteps || [];
    const initialValues = {};
    steps.forEach(step => {
      (step.arithmeticFields || []).forEach(f => {
        if (fieldValues[f.id] === undefined && fieldValues[f.id] !== 0) {
          if (f.fieldType === 'constant') {
            initialValues[f.id] = f.constantValue !== undefined && f.constantValue !== null ? f.constantValue : 0;
          } else {
            initialValues[f.id] = 0;
          }
        }
      });
    });
    if (Object.keys(initialValues).length > 0) setFieldValues(prev => ({ ...prev, ...initialValues }));
  }, [field.formulaSteps]);

  // ==================== HELPER FUNCTIONS - PURE (NO setState) ====================
  
  // Calculate arithmetic result - PURE FUNCTION
  const calculateArithmeticResult = useCallback((arithmeticField, allFieldValues) => {
    if (!arithmeticField.arithmeticFields || arithmeticField.arithmeticFields.length === 0) {
      return 0;
    }
    
    let result = 0;
    let isFirst = true;
    const operator = arithmeticField.arithmeticOperator || 'add';
    
    for (const fieldData of arithmeticField.arithmeticFields) {
      let value;
      
      if (fieldData.isSharedReference) {
        continue;
      }
      
      if (fieldData.fieldType === 'constant') {
        value = fieldData.constantValue !== undefined && fieldData.constantValue !== null ? parseFloat(fieldData.constantValue) : 0;
      } else {
        value = allFieldValues[fieldData.id] !== undefined ? allFieldValues[fieldData.id] : 0;
      }
      
      if (isFirst) {
        result = value;
        isFirst = false;
      } else {
        switch (operator) {
          case 'add': result += value; break;
          case 'subtract': result -= value; break;
          case 'multiply': result *= value; break;
          case 'divide': result = value !== 0 ? result / value : 0; break;
          case 'percentage': result = value !== 0 ? (result / value) * 100 : 0; break;
          case 'equals': result = (result === value) ? 1 : 0; break;
          case 'not_equals': result = (result !== value) ? 1 : 0; break;
          case 'greater_than': result = (result > value) ? 1 : 0; break;
          case 'less_than': result = (result < value) ? 1 : 0; break;
          case 'greater_than_equal': result = (result >= value) ? 1 : 0; break;
          case 'less_than_equal': result = (result <= value) ? 1 : 0; break;
          default: result += value;
        }
      }
    }
    
    return result;
  }, []);

  // Calculate formula result - PURE FUNCTION (NO setState)
  const calculateFormulaResult = useCallback((formulaField, allFieldValues, visitedFields = new Set()) => {
    // Prevent circular reference
    if (visitedFields.has(formulaField.id)) {
      console.warn(`Circular reference detected for formula field ${formulaField.id}`);
      return 0;
    }
    
    visitedFields.add(formulaField.id);
    
    if (!formulaField.formulaSteps || formulaField.formulaSteps.length === 0) {
      visitedFields.delete(formulaField.id);
      return 0;
    }
    
    const stepResultsArray = [];
    
    for (const step of formulaField.formulaSteps) {
      const fields = step.arithmeticFields || [];
      if (fields.length === 0) {
        stepResultsArray.push(0);
        continue;
      }
      
      let stepResult = 0;
      let isFirst = true;
      const operator = step.combineOperator || 'add';
      
      for (const fieldData of fields) {
        let value;
        
        // Handle shared/external field references
        if (fieldData.isSharedReference === true || fieldData.isExternalReference === true) {
          const sourceId = fieldData.sourceFieldId || fieldData.id;
          
          // First check if value exists in allFieldValues
          if (allFieldValues[sourceId] !== undefined && allFieldValues[sourceId] !== null) {
            value = allFieldValues[sourceId];
          }
          // For formula fields - calculate recursively
          else if (fieldData.sourceFieldType === 'formula') {
            const originalFormulaField = allFields?.find(f => f.id === sourceId);
            if (originalFormulaField && originalFormulaField.type === 'formula') {
              value = calculateFormulaResult(originalFormulaField, allFieldValues, visitedFields);
            } else {
              value = 0;
            }
          }
          // For arithmetic fields
          else if (fieldData.sourceFieldType === 'arithmetic') {
            const originalArithmeticField = allFields?.find(f => f.id === sourceId);
            if (originalArithmeticField && originalArithmeticField.type === 'arithmetic') {
              value = calculateArithmeticResult(originalArithmeticField, allFieldValues);
            } else {
              value = 0;
            }
          }
          else {
            value = allFieldValues[sourceId] !== undefined ? allFieldValues[sourceId] : 0;
          }
        }
        else if (fieldData.fieldType === 'constant') {
          value = fieldData.constantValue !== undefined && fieldData.constantValue !== null ? parseFloat(fieldData.constantValue) : 0;
        }
        else {
          value = allFieldValues[fieldData.id] !== undefined ? allFieldValues[fieldData.id] : 0;
        }
        
        if (isFirst) {
          stepResult = value;
          isFirst = false;
        } else {
          switch (operator) {
            case 'add': stepResult += value; break;
            case 'subtract': stepResult -= value; break;
            case 'multiply': stepResult *= value; break;
            case 'divide': stepResult = value !== 0 ? stepResult / value : 0; break;
            case 'percentage': stepResult = value !== 0 ? (stepResult / value) * 100 : 0; break;
            case 'equals': stepResult = (stepResult === value) ? 1 : 0; break;
            case 'not_equals': stepResult = (stepResult !== value) ? 1 : 0; break;
            case 'greater_than': stepResult = (stepResult > value) ? 1 : 0; break;
            case 'less_than': stepResult = (stepResult < value) ? 1 : 0; break;
            case 'greater_than_equal': stepResult = (stepResult >= value) ? 1 : 0; break;
            case 'less_than_equal': stepResult = (stepResult <= value) ? 1 : 0; break;
            default: stepResult += value;
          }
        }
      }
      stepResultsArray.push(stepResult);
    }
    
    const combineOp = formulaField.stepCombineOperator || 'add';
    
    let finalResult;
    if (stepResultsArray.length === 1 || combineOp === 'none') {
      finalResult = stepResultsArray[0];
    } else {
      finalResult = stepResultsArray[0];
      for (let i = 1; i < stepResultsArray.length; i++) {
        switch (combineOp) {
          case 'add': finalResult += stepResultsArray[i]; break;
          case 'subtract': finalResult -= stepResultsArray[i]; break;
          case 'multiply': finalResult *= stepResultsArray[i]; break;
          case 'divide': finalResult = stepResultsArray[i] !== 0 ? finalResult / stepResultsArray[i] : 0; break;
          case 'percentage': finalResult = stepResultsArray[i] !== 0 ? (finalResult / stepResultsArray[i]) * 100 : 0; break;
          case 'equals': finalResult = (finalResult === stepResultsArray[i]) ? 1 : 0; break;
          case 'not_equals': finalResult = (finalResult !== stepResultsArray[i]) ? 1 : 0; break;
          case 'greater_than': finalResult = (finalResult > stepResultsArray[i]) ? 1 : 0; break;
          case 'less_than': finalResult = (finalResult < stepResultsArray[i]) ? 1 : 0; break;
          case 'greater_than_equal': finalResult = (finalResult >= stepResultsArray[i]) ? 1 : 0; break;
          case 'less_than_equal': finalResult = (finalResult <= stepResultsArray[i]) ? 1 : 0; break;
          default: finalResult += stepResultsArray[i];
        }
      }
    }
    
    visitedFields.delete(formulaField.id);
    return finalResult;
  }, [allFields, calculateArithmeticResult]);


const calculateStep = useCallback((step, currentFieldValues, currentStepResults) => {
  const fields = step.arithmeticFields || [];
  if (fields.length === 0) return 0;

  let result = 0;
  let isFirst = true;
  const operator = step.combineOperator || 'add';

  for (const fieldData of fields) {
    let value = 0;
    
    // Handle shared/external field references (including formula fields)
if (fieldData.isSharedReference === true || fieldData.isExternalReference === true) {
    const sourceId = fieldData.sourceFieldId || fieldData.id;
    
    // Check if this is a step result reference
    if (fieldData.isStepResultReference === true && fieldData.sourceStepId) {
        const sourceStep = (field.formulaSteps || []).find(s => s.id === fieldData.sourceStepId);
        if (sourceStep && currentStepResults[sourceStep.resultVariable] !== undefined) {
            value = currentStepResults[sourceStep.resultVariable];
            console.log(`Step result ${sourceStep.name}: ${value}`); // Debug
        } else {
            value = 0;
        }
    }
    // First check fieldValues for stored value from any field
    else if (currentFieldValues[sourceId] !== undefined && currentFieldValues[sourceId] !== null) {
        value = currentFieldValues[sourceId];
    }
      // For formula result fields - calculate if not stored
      else if (fieldData.sourceFieldType === 'formula') {
        const originalFormulaField = allFields?.find(f => f.id === sourceId);
        if (originalFormulaField && originalFormulaField.type === 'formula') {
          value = calculateFormulaResult(originalFormulaField, currentFieldValues);
        } else {
          value = currentFieldValues[sourceId] !== undefined ? currentFieldValues[sourceId] : 0;
        }
      }
      // For arithmetic result fields
      else if (fieldData.sourceFieldType === 'arithmetic') {
        const originalArithmeticField = allFields?.find(f => f.id === sourceId);
        if (originalArithmeticField && originalArithmeticField.type === 'arithmetic') {
          value = calculateArithmeticResult(originalArithmeticField, currentFieldValues);
        } else {
          value = currentFieldValues[sourceId] !== undefined ? currentFieldValues[sourceId] : 0;
        }
      }
      // For regular number fields
      else {
        value = currentFieldValues[sourceId] !== undefined ? currentFieldValues[sourceId] : 0;
      }
    }
    // Handle constant fields
    else if (fieldData.fieldType === 'constant') {
      value = fieldData.constantValue !== undefined && fieldData.constantValue !== null ? parseFloat(fieldData.constantValue) : 0;
    }
    // Handle regular user input fields
    else {
      // CRITICAL FIX: Safely access fieldValues with optional chaining and null check
      value = (currentFieldValues && currentFieldValues[fieldData.id] !== undefined && currentFieldValues[fieldData.id] !== null) 
        ? currentFieldValues[fieldData.id] 
        : 0;
    }

    // Apply validation rules
    const validation = fieldData.validation || {};
    if (validation.allowNegative === false && value < 0) value = 0;
    if (validation.allowZero === false && value === 0) value = 0.01;
    if (validation.integerOnly) value = Math.floor(value);
    if (validation.decimalPlaces !== null && validation.decimalPlaces !== undefined && validation.allowDecimals) {
      value = parseFloat(value.toFixed(validation.decimalPlaces));
    }

    // Perform the operation
    if (isFirst) {
      result = value;
      isFirst = false;
    } else {
      switch (operator) {
        case 'add': result += value; break;
        case 'subtract': result -= value; break;
        case 'multiply': result *= value; break;
        case 'divide': result = value !== 0 ? result / value : 0; break;
        case 'percentage': result = value !== 0 ? (result / value) * 100 : 0; break;
        case 'equals': result = (result === value) ? 1 : 0; break;
        case 'not_equals': result = (result !== value) ? 1 : 0; break;
        case 'greater_than': result = (result > value) ? 1 : 0; break;
        case 'less_than': result = (result < value) ? 1 : 0; break;
        case 'greater_than_equal': result = (result >= value) ? 1 : 0; break;
        case 'less_than_equal': result = (result <= value) ? 1 : 0; break;
        default: result += value;
      }
    }
  }
  return result;
}, [allFields, calculateFormulaResult, calculateArithmeticResult, field.formulaSteps]);
  // ==================== UI ACTION FUNCTIONS (with setState) ====================

  const addStep = useCallback(() => {
    const steps = field.formulaSteps || [];
    const newStep = { 
      id: Date.now(), 
      name: `Step ${steps.length + 1}`, 
      arithmeticFields: [], 
      combineOperator: 'add', 
      resultVariable: `step_${steps.length + 1}_result`, 
      resultLabel: { en: `Step ${steps.length + 1} Result`, mr: `पायरी ${steps.length + 1} चे निकाल` },
      showStepResult: true,
      falseMessage: ''
    };
    updateField('formulaSteps', [...steps, newStep]);
  }, [field.formulaSteps, updateField]);

  const deleteStep = useCallback((stepId) => {
    const steps = (field.formulaSteps || []).filter(step => step.id !== stepId);
    const renamedSteps = steps.map((step, idx) => ({ 
      ...step, 
      name: `Step ${idx + 1}`, 
      resultVariable: `step_${idx + 1}_result`, 
      resultLabel: { en: `Step ${idx + 1} Result`, mr: `पायरी ${idx + 1} चे निकाल` } 
    }));
    updateField('formulaSteps', renamedSteps);
  }, [field.formulaSteps, updateField]);

  const updateStepName = useCallback((stepId, name) => {
    const steps = (field.formulaSteps || []).map(step => step.id === stepId ? { ...step, name } : step);
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const updateStepResultLabel = useCallback((stepId, lang, value) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === stepId) {
        const currentLabel = step.resultLabel || { en: `${step.name} Result`, mr: `${step.name} चे निकाल` };
        return { ...step, resultLabel: { ...currentLabel, [lang === 'english' ? 'en' : 'mr']: value } };
      }
      return step;
    });
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const updateStepCombineOperator = useCallback((stepId, operator) => {
    const steps = (field.formulaSteps || []).map(step => step.id === stepId ? { ...step, combineOperator: operator } : step);
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const updateStepShowResult = useCallback((stepId, show) => {
    const steps = (field.formulaSteps || []).map(step => step.id === stepId ? { ...step, showStepResult: show } : step);
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const updateStepFalseMessage = useCallback((stepId, message) => {
    const steps = (field.formulaSteps || []).map(step => step.id === stepId ? { ...step, falseMessage: message } : step);
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const updateArithmeticFieldFalseMessage = useCallback((stepId, fieldIndex, message) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === stepId) {
        const newFields = [...(step.arithmeticFields || [])];
        if (newFields[fieldIndex]) {
          newFields[fieldIndex] = { ...newFields[fieldIndex], falseMessage: message };
        }
        return { ...step, arithmeticFields: newFields };
      }
      return step;
    });
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const openFalseMessageModal = useCallback((stepId, type, arithmeticFieldId = null, arithmeticFieldIndex = null) => {
    setCurrentFalseMessageStepId(stepId);
    setCurrentFalseMessageType(type);
    setCurrentArithmeticFieldId(arithmeticFieldIndex);
    setFalseMessageModalVisible(true);
  }, []);

const addArithmeticField = useCallback((stepId) => {
  const newField = {
    id: uid(),
    type: 'number',
    fieldType: 'userInput',
    constantValue: 0,
    enLabel: `Value ${Math.floor(Math.random() * 100) + 1}`,
    mrLabel: `मूल्य ${Math.floor(Math.random() * 100) + 1}`,
    enPlaceholder: 'Enter value',
    mrPlaceholder: 'मूल्य प्रविष्ट करा',
    enRequired: false,
    value: 0,
    hasUnit: false,
    selectedUnit: null,
    selectedUnitObject: null,
    units: { enabled: false },
    falseMessage: '',
    validation: {
      exactLength: null,
      decimalPlaces: null,
      allowDecimals: true,
      allowNegative: false,
      allowZero: true,
      integerOnly: false,
      allowSpaces: true,
      trimInput: true,
      allowLeadingSpaces: false,
      allowTrailingSpaces: false,
      allowMultipleSpaces: true,
      collapseSpaces: false
    }
  };

  // Update fieldValues FIRST before updating formulaSteps
  setFieldValues(prev => ({ ...prev, [newField.id]: newField.constantValue }));

  // Then update the steps
  const steps = (field.formulaSteps || []).map(step => {
    if (step.id === stepId) {
      return { ...step, arithmeticFields: [...(step.arithmeticFields || []), newField] };
    }
    return step;
  });
  updateField('formulaSteps', steps);
}, [field.formulaSteps, updateField]);

  const updateArithmeticField = useCallback((stepId, fieldIndex, key, value) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === stepId) {
        const newFields = [...(step.arithmeticFields || [])];
        newFields[fieldIndex] = { ...newFields[fieldIndex], [key]: value };
        return { ...step, arithmeticFields: newFields };
      }
      return step;
    });
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const updateArithmeticFieldComplete = useCallback((stepId, fieldIndex, updatedField) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === stepId) {
        const newFields = [...(step.arithmeticFields || [])];
        newFields[fieldIndex] = updatedField;
        if (updatedField.fieldType === 'constant') {
          const constValue = updatedField.constantValue !== undefined && updatedField.constantValue !== null ? updatedField.constantValue : 0;
          setFieldValues(prev => ({ ...prev, [updatedField.id]: constValue }));
        }
        return { ...step, arithmeticFields: newFields };
      }
      return step;
    });
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const removeArithmeticField = useCallback((stepId, fieldIndex) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === stepId) {
        const newFields = (step.arithmeticFields || []).filter((_, idx) => idx !== fieldIndex);
        return { ...step, arithmeticFields: newFields };
      }
      return step;
    });
    updateField('formulaSteps', steps);
  }, [field.formulaSteps, updateField]);

  const addSharedFieldToStep = useCallback(({ targetStepId, sourceFieldId, fieldData, operator }) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === targetStepId) {
        const sharedField = {
          ...fieldData,
          id: sourceFieldId,
          isSharedReference: true,
          sourceStepId: selectedSourceStepId,
          sharedOperator: operator,
          falseMessage: ''
        };
        return {
          ...step,
          arithmeticFields: [...(step.arithmeticFields || []), sharedField]
        };
      }
      return step;
    });
    updateField('formulaSteps', steps);
    message.success(
      activeLanguage === "english"
        ? `Field "${fieldData.enLabel}" added to target step`
        : `फील्ड "${fieldData.mrLabel}" लक्ष्य पायरीमध्ये जोडले गेले`
    );
  }, [field.formulaSteps, updateField, activeLanguage, selectedSourceStepId]);

  const addSharedStepResultToStep = useCallback(({ targetStepId, sourceStepId, sourceStepName, stepResultVariable, stepLabel, operator }) => {
    const sourceStep = (field.formulaSteps || []).find(s => s.id === sourceStepId);
    if (!sourceStep) {
      message.error(activeLanguage === "english" ? "Source step not found" : "स्रोत पायरी सापडली नाही");
      return;
    }

    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === targetStepId) {
        const sharedStepResultField = {
          id: `step_result_${sourceStepId}_${Date.now()}`,
          type: 'number',
          fieldType: 'stepResultReference',
          isSharedReference: true,
          isStepResultReference: true,
          sourceStepId: sourceStepId,
          sourceStepName: sourceStepName,
          stepResultVariable: stepResultVariable,
          sharedOperator: operator,
          enLabel: `${sourceStepName} Result`,
          mrLabel: `${sourceStepName} निकाल`,
          enPlaceholder: 'Auto-calculated step result',
          mrPlaceholder: 'आपोआप गणना केलेला पायरी निकाल',
          enRequired: false,
          hasUnit: false,
          selectedUnit: null,
          selectedUnitObject: null,
          units: { enabled: false },
          falseMessage: '',
          isResultOnly: true,
          sourceFieldType: 'stepResult'
        };
        
        return {
          ...step,
          arithmeticFields: [...(step.arithmeticFields || []), sharedStepResultField]
        };
      }
      return step;
    });
    
    updateField('formulaSteps', steps);
    message.success(
      activeLanguage === "english"
        ? `Step result "${sourceStepName}" added to target step`
        : `पायरीचा निकाल "${sourceStepName}" लक्ष्य पायरीमध्ये जोडला गेला`
    );
  }, [field.formulaSteps, updateField, activeLanguage]);

  const addExternalNumberFieldToStep = useCallback((stepId, externalField) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === stepId) {
        let sharedField;
        
        if (externalField.type === 'formula') {
          sharedField = {
            id: externalField.id,
            type: 'number',
            fieldType: 'formulaResult',
            isSharedReference: true,
            isExternalReference: true,
            isResultOnly: true,
            sourceFieldId: externalField.id,
            sourceFieldType: externalField.type,
            sourceFieldLabel: externalField.enLabel,
            enLabel: `${externalField.enLabel || externalField.label} (Result)`,
            mrLabel: `${externalField.mrLabel || externalField.label} (निकाल)`,
            enPlaceholder: 'Formula result (auto-calculated)',
            mrPlaceholder: 'फॉर्म्युला निकाल (आपोआप गणना)',
            enRequired: externalField.enRequired || false,
            hasUnit: externalField.hasUnit || false,
            selectedUnit: externalField.selectedUnit,
            selectedUnitObject: externalField.selectedUnitObject,
            units: externalField.units || { enabled: false },
            validation: externalField.validation || {},
            sharedOperator: step.combineOperator || 'add',
            falseMessage: '',
            originalFormulaSteps: externalField.formulaSteps,
            originalStepCombineOperator: externalField.stepCombineOperator,
            originalShowResult: externalField.showResult
          };
        } 
        else if (externalField.type === 'arithmetic') {
          sharedField = {
            id: externalField.id,
            type: 'number',
            fieldType: 'arithmeticResult',
            isSharedReference: true,
            isExternalReference: true,
            isResultOnly: true,
            sourceFieldId: externalField.id,
            sourceFieldType: externalField.type,
            sourceFieldLabel: externalField.enLabel,
            enLabel: `${externalField.enLabel || externalField.label} (Result)`,
            mrLabel: `${externalField.mrLabel || externalField.label} (निकाल)`,
            enPlaceholder: 'Arithmetic result (auto-calculated)',
            mrPlaceholder: 'अंकगणित निकाल (आपोआप गणना)',
            enRequired: externalField.enRequired || false,
            hasUnit: externalField.hasUnit || false,
            selectedUnit: externalField.selectedUnit,
            selectedUnitObject: externalField.selectedUnitObject,
            units: externalField.units || { enabled: false },
            validation: externalField.validation || {},
            sharedOperator: step.combineOperator || 'add',
            falseMessage: '',
            originalArithmeticFields: externalField.arithmeticFields,
            originalArithmeticOperator: externalField.arithmeticOperator,
            originalResultField: externalField.resultField
          };
        }
        else {
          sharedField = {
            id: externalField.id,
            type: externalField.type,
            fieldType: 'externalReference',
            isSharedReference: true,
            isExternalReference: true,
            isResultOnly: false,
            sourceFieldId: externalField.id,
            sourceFieldType: externalField.type,
            sourceFieldLabel: externalField.enLabel,
            enLabel: externalField.enLabel || externalField.label,
            mrLabel: externalField.mrLabel || externalField.label,
            enPlaceholder: externalField.enPlaceholder || '',
            mrPlaceholder: externalField.mrPlaceholder || '',
            enRequired: externalField.enRequired || false,
            hasUnit: externalField.hasUnit || false,
            selectedUnit: externalField.selectedUnit,
            selectedUnitObject: externalField.selectedUnitObject,
            units: externalField.units || { enabled: false },
            validation: externalField.validation || {},
            sharedOperator: step.combineOperator || 'add',
            falseMessage: ''
          };
        }

        if (externalField.value !== undefined && externalField.value !== null) {
          setFieldValues(prev => ({ ...prev, [externalField.id]: externalField.value }));
        }

        return {
          ...step,
          arithmeticFields: [...(step.arithmeticFields || []), sharedField]
        };
      }
      return step;
    });

    updateField('formulaSteps', steps);
    
    message.success(
      activeLanguage === "english"
        ? `Field "${externalField.enLabel}" added to formula (auto-calculated value)`
        : `फील्ड "${externalField.mrLabel}" फॉर्म्युलामध्ये जोडले (आपोआप गणना मूल्य)`
    );
  }, [field.formulaSteps, updateField, activeLanguage]);

  const editField = useCallback((stepId, fieldIndex, fieldData) => {
    setEditingField(fieldData);
    setEditingStepId(stepId);
    setEditingFieldIndex(fieldIndex);
    setShowFieldConfigModal(true);
  }, []);

  const openAddToStepModal = useCallback((stepId, fieldIndex, fieldData) => {
    setSelectedFieldForSharing(fieldData);
    setSelectedSourceStepId(stepId);
    setSelectedSourceFieldIndex(fieldIndex);
    setAddToStepModalVisible(true);
  }, []);

  const openShareStepResultModal = useCallback((stepId) => {
    const sourceStep = (field.formulaSteps || []).find(s => s.id === stepId);
    if (sourceStep) {
      setSelectedSourceStepForSharing(sourceStep);
      setShareStepResultModalVisible(true);
    }
  }, [field.formulaSteps]);

  const openExternalFieldsModal = useCallback((stepId, existingFields) => {
    setCurrentStepIdForExternal(stepId);
    setCurrentStepFields(existingFields);
    setShowExternalFieldsModal(true);
  }, []);

  const updateFieldConfig = useCallback((updatedField) => {
    const steps = (field.formulaSteps || []).map(step => {
      if (step.id === editingStepId) {
        const newFields = [...(step.arithmeticFields || [])];
        newFields[editingFieldIndex] = {
          ...updatedField,
          enRequired: updatedField.enRequired === true,
          required: updatedField.enRequired === true
        };
        return { ...step, arithmeticFields: newFields };
      }
      return step;
    });
    updateField('formulaSteps', steps);
    setShowFieldConfigModal(false);
    setEditingField(null);
    setEditingStepId(null);
    setEditingFieldIndex(null);
  }, [field.formulaSteps, editingStepId, editingFieldIndex, updateField]);

  const updateStepCombineOperatorGlobal = useCallback((operator) => {
    updateField('stepCombineOperator', operator);
  }, [updateField]);

  const updateFieldValue = useCallback((fieldId, value) => {
    const allSteps = field.formulaSteps || [];
    let isConstant = false;
    let constantFieldData = null;
    let isResultOnly = false;

    for (const step of allSteps) {
      const foundField = (step.arithmeticFields || []).find(f => f.id === fieldId);
      if (foundField) {
        if (foundField.fieldType === 'constant') {
          isConstant = true;
          constantFieldData = foundField;
        } else if (foundField.isResultOnly === true) {
          isResultOnly = true;
        }
        break;
      }
    }

    if (isResultOnly) {
      message.warning(activeLanguage === "english" 
        ? "Result fields are auto-calculated and cannot be edited directly"
        : "निकाल फील्ड आपोआप गणना केले जातात आणि थेट संपादित करता येत नाहीत");
      return;
    }

    if (isConstant) {
      if (constantFieldData && constantFieldData.constantValue !== undefined) {
        const constValue = constantFieldData.constantValue;
        setFieldValues(prev => ({ ...prev, [fieldId]: constValue }));
        if (onFieldValueChange) {
          onFieldValueChange(fieldId, constValue);
        }
      }
      return;
    }

    const fieldData = allSteps.flatMap(step => step.arithmeticFields || []).find(f => f.id === fieldId);
    let processedValue = value;
    let error = null;
    
    if (fieldData) {
      const validationResult = applyNumberValidation(fieldData, value);
      processedValue = validationResult.transformed;
      error = validationResult.error;
      
      if (error) {
        setPreviewErrors(prev => ({ ...prev, [fieldId]: error }));
      } else {
        setPreviewErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });
      }
    }
    
    const numValue = parseFloat(processedValue) || 0;
    setFieldValues(prev => ({ ...prev, [fieldId]: numValue }));
    if (onFieldValueChange) {
      onFieldValueChange(fieldId, numValue);
    }
  }, [field.formulaSteps, onFieldValueChange, activeLanguage]);

const calculateAllSteps = useCallback(() => {
  const steps = field.formulaSteps || [];
  if (steps.length === 0) return 0;

  const results = {};
  const stepResultsArray = [];
  
  // Ensure fieldValues is defined
  const currentFieldValues = fieldValues || {};

  for (const step of steps) {
    const result = calculateStep(step, currentFieldValues, stepResults);
    results[step.resultVariable] = result;
    stepResultsArray.push(result);
  }

  // Only update stepResults if they changed
  let shouldUpdateStepResults = false;
  for (const key of Object.keys(results)) {
    if (stepResults[key] !== results[key]) {
      shouldUpdateStepResults = true;
      break;
    }
  }
  if (shouldUpdateStepResults) {
    setStepResults(results);
  }

  const combineOp = field.stepCombineOperator || 'add';
  
  let finalResult;
  if (steps.length === 1 || combineOp === 'none') {
    finalResult = stepResultsArray[0];
  } else {
    finalResult = stepResultsArray[0];
    for (let i = 1; i < stepResultsArray.length; i++) {
      switch (combineOp) {
        case 'add': finalResult += stepResultsArray[i]; break;
        case 'subtract': finalResult -= stepResultsArray[i]; break;
        case 'multiply': finalResult *= stepResultsArray[i]; break;
        case 'divide': finalResult = stepResultsArray[i] !== 0 ? finalResult / stepResultsArray[i] : 0; break;
        case 'percentage': finalResult = stepResultsArray[i] !== 0 ? (finalResult / stepResultsArray[i]) * 100 : 0; break;
        case 'equals': finalResult = finalResult === stepResultsArray[i] ? 1 : 0; break;
        case 'not_equals': finalResult = finalResult !== stepResultsArray[i] ? 1 : 0; break;
        case 'greater_than': finalResult = finalResult > stepResultsArray[i] ? 1 : 0; break;
        case 'less_than': finalResult = finalResult < stepResultsArray[i] ? 1 : 0; break;
        case 'greater_than_equal': finalResult = finalResult >= stepResultsArray[i] ? 1 : 0; break;
        case 'less_than_equal': finalResult = finalResult <= stepResultsArray[i] ? 1 : 0; break;
        default: finalResult += stepResultsArray[i];
      }
    }
  }

  // Update fieldValues with the final result only if changed
  if (finalResult !== undefined && finalResult !== null) {
    setFieldValues(prev => {
      const current = prev || {};
      if (current[field.id] !== finalResult) {
        return { ...current, [field.id]: finalResult };
      }
      return current;
    });
  }

  return finalResult;
}, [field.formulaSteps, field.stepCombineOperator, calculateStep, fieldValues, stepResults, field.id]);

  const finalResult = useMemo(() => calculateAllSteps(), [calculateAllSteps]);
  const steps = field.formulaSteps || [];
  const stepCombineOp = field.stepCombineOperator || 'add';
  const isLogicalCombine = ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_equal', 'less_than_equal'].includes(stepCombineOp);
  const isSingleStep = steps.length === 1;

  const getFormattedResult = useCallback((result, isLogical, falseMessage = '') => {
    if (!isLogical) {
      if (typeof result === 'number' && !isNaN(result)) {
        return result.toFixed(2);
      }
      return result;
    }
    
    if (result === 1) {
      return '';
    } else {
      return falseMessage || '';
    }
  }, []);

  const getStepDisplayFormula = useCallback((step) => {
    const fields = step.arithmeticFields || [];
    if (fields.length === 0) return 'No fields added';
    const operator = step.combineOperator || 'add';

    let opSymbol = '+';
    switch (operator) {
      case 'add': opSymbol = '+'; break;
      case 'subtract': opSymbol = '-'; break;
      case 'multiply': opSymbol = '×'; break;
      case 'divide': opSymbol = '÷'; break;
      case 'percentage': opSymbol = '%'; break;
      case 'equals': opSymbol = '=='; break;
      case 'not_equals': opSymbol = '≠'; break;
      case 'greater_than': opSymbol = '>'; break;
      case 'less_than': opSymbol = '<'; break;
      case 'greater_than_equal': opSymbol = '≥'; break;
      case 'less_than_equal': opSymbol = '≤'; break;
      default: opSymbol = '+';
    }

    return fields.map((f, idx) => {
      if (f.isSharedReference && !f.isResultOnly) return '';
      let label = activeLanguage === 'english' ? (f.enLabel || 'Value') : (f.mrLabel || 'मूल्य');
      if (f.fieldType === 'constant') {
        label = `${label} [${f.constantValue}]`;
      }
      if (f.isResultOnly) {
        label = `[Auto] ${label}`;
      }
      if (f.isStepResultReference) {
        label = `[Step Result] ${label}`;
      }
      const op = idx > 0 ? ` ${opSymbol} ` : '';
      return `${op}${label}`;
    }).filter(item => item !== '').join('');
  }, [activeLanguage]);

  const getOperatorSymbol = (op) => {
    switch (op) {
      case 'add': return '+';
      case 'subtract': return '-';
      case 'multiply': return '×';
      case 'divide': return '÷';
      case 'percentage': return '%';
      case 'equals': return '==';
      case 'not_equals': return '≠';
      case 'greater_than': return '>';
      case 'less_than': return '<';
      case 'greater_than_equal': return '≥';
      case 'less_than_equal': return '≤';
      default: return '+';
    }
  };

  const getCombineSymbol = () => {
    const op = STEP_COMBINE_OPERATORS.find(o => o.value === stepCombineOp);
    return op?.symbol || '+';
  };

  const getUnitDisplay = (fieldData) => {
    if (!fieldData) return null;
    if (fieldData.hasUnit && fieldData.selectedUnitObject) {
      return fieldData.selectedUnitObject.unit_symbol || fieldData.selectedUnitObject.unit_name;
    }
    if (fieldData.units?.enabled && fieldData.units.selectedUnitSymbol) {
      return fieldData.units.selectedUnitSymbol;
    }
    return null;
  };

  const buildActiveRules = (fieldData) => {
    const rules = [];
    const v = fieldData.validation || {};

    if (v.exactLength) rules.push({ color: "orange", text: `Exact: ${v.exactLength} digits` });
    if (v.decimalPlaces && v.decimalPlaces > 0) rules.push({ color: "cyan", text: `${v.decimalPlaces} decimal(s)` });
    if (v.allowDecimals === false) rules.push({ color: "orange", text: "No decimals" });
    if (v.allowNegative === false) rules.push({ color: "orange", text: "Positive only" });
    if (v.allowZero === false) rules.push({ color: "red", text: "Zero not allowed" });
    if (v.integerOnly) rules.push({ color: "purple", text: "Integer only" });

    const unit = getUnitDisplay(fieldData);
    if (unit) rules.push({ color: "blue", text: `Unit: ${unit}` });

    if (fieldData.isSharedReference) {
      rules.push({ color: "gold", text: "Shared Field" });
    }
    
    if (fieldData.isResultOnly) {
      if (fieldData.sourceFieldType === 'formula') {
        rules.push({ color: "purple", text: "Formula Result (Auto)" });
      } else if (fieldData.sourceFieldType === 'arithmetic') {
        rules.push({ color: "orange", text: "Arithmetic Result (Auto)" });
      } else if (fieldData.isStepResultReference) {
        rules.push({ color: "magenta", text: "Step Result (Auto)" });
      }
    }

    if (fieldData.fieldType === 'constant') {
      rules.push({ color: "purple", text: `Constant: ${fieldData.constantValue}` });
    }

    return rules;
  };

  const renderStep = (step, index) => {
    const stepResult = stepResults[step.resultVariable] || 0;
    const displayFormula = getStepDisplayFormula(step);
    const fields = step.arithmeticFields || [];
    const isMainResult = isSingleStep;
    const stepIsLogical = isLogicalOperator(step.combineOperator);
    const showStepResultValue = step.showStepResult !== false;
    
    const stepResultWords = !stepIsLogical ? getWordsRepresentation(stepResult) : '';
    const stepDisplayValue = !stepIsLogical ? stepResult.toFixed(2) : stepResult;

    return (
      <Card key={step.id} size="small" style={{ marginBottom: 16, background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: '8px' }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Badge count={index + 1} style={{ backgroundColor: '#1890ff' }} />
            <Input value={step.name} onChange={(e) => updateStepName(step.id, e.target.value)} style={{ width: 100 }} size="small" placeholder="Step name" disabled={disabled} />
            <Select value={step.combineOperator || 'add'} onChange={(value) => updateStepCombineOperator(step.id, value)} style={{ width: 120 }} size="small" disabled={disabled}>
              <Select.OptGroup label="Arithmetic">
                <Option value="add">+ (Add)</Option>
                <Option value="subtract">- (Subtract)</Option>
                <Option value="multiply">× (Multiply)</Option>
                <Option value="divide">÷ (Divide)</Option>
                <Option value="percentage">% (Percentage)</Option>
              </Select.OptGroup>
              <Select.OptGroup label="Logical">
                <Option value="equals">== (Equals)</Option>
                <Option value="not_equals">≠ (Not Equals)</Option>
                <Option value="greater_than">&gt; (Greater Than)</Option>
                <Option value="less_than">&lt; (Less Than)</Option>
                <Option value="greater_than_equal">≥ (Greater or Equal)</Option>
                <Option value="less_than_equal">≤ (Less or Equal)</Option>
              </Select.OptGroup>
            </Select>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => addArithmeticField(step.id)} disabled={disabled} style={{ background: '#52c41a' }}>Add Number Field</Button>
            <Button type="default" size="small" icon={<DatabaseOutlined />} onClick={() => openExternalFieldsModal(step.id, step.arithmeticFields || [])} disabled={disabled}>
              {activeLanguage === 'english' ? 'Add External Field' : 'बाह्य फील्ड जोडा'}
            </Button>
            {/* NEW: Share Step Result button */}
            {steps.length > 1 && (
              <Button 
                type="default" 
                size="small" 
                icon={<ShareAltOutlined style={{ color: '#722ed1' }} />} 
                onClick={() => openShareStepResultModal(step.id)} 
                disabled={disabled}
                style={{ borderColor: '#722ed1', color: '#722ed1' }}
              >
                {activeLanguage === 'english' ? 'Share Result' : 'निकाल शेअर करा'}
              </Button>
            )}
            {steps.length > 1 && <Button type="text" size="small" icon={<DeleteOutlined />} onClick={() => deleteStep(step.id)} danger disabled={disabled} />}
          </div>
        }
      >
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
          <Space>
            <Text strong>Formula: </Text>
            <Text code>{displayFormula || 'No visible fields'}</Text>
          </Space>
        </div>

        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space>
            <EyeOutlined />
            <Text>{activeLanguage === 'english' ? 'Show Step Result:' : 'पायरीचा निकाल दाखवा:'}</Text>
            <Switch
              checked={showStepResultValue}
              onChange={(checked) => updateStepShowResult(step.id, checked)}
              size="small"
              disabled={disabled}
            />
            {!showStepResultValue && <Tag color="orange" size="small">{activeLanguage === 'english' ? 'Result Hidden' : 'निकाल लपविला'}</Tag>}
          </Space>
          
          {stepIsLogical && (
            <Button
              size="small"
              icon={<MessageOutlined />}
              onClick={() => openFalseMessageModal(step.id, 'step')}
              disabled={disabled}
            >
              {activeLanguage === 'english' ? 'False Message' : 'फॉल्स मेसेज'}
            </Button>
          )}
        </div>

        {fields.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}><Text type="secondary">No number fields added. Click "Add Number Field" to add fields.</Text></div>
        ) : (
          fields.map((fieldData, fieldIndex) => {
            if (fieldData.isSharedReference) {
              const isExternal = fieldData.isExternalReference === true;
              const isResultOnly = fieldData.isResultOnly === true;
              const isStepResultRef = fieldData.isStepResultReference === true;
              const resultType = fieldData.sourceFieldType === 'formula' ? 'Formula Result' : 
                                 fieldData.sourceFieldType === 'arithmetic' ? 'Arithmetic Result' : 
                                 isStepResultRef ? 'Step Result' : 'External';
              
              return (
                <div key={fieldData.id} style={{ marginBottom: 12, padding: '10px 12px', background: isStepResultRef ? '#f9f0ff' : (isResultOnly ? '#f0fdf4' : (isExternal ? '#f0fdf4' : '#e6f7ff')), borderRadius: '6px', border: isStepResultRef ? '1px solid #d3adf7' : (isResultOnly ? '1px solid #52c41a' : (isExternal ? '1px solid #b7eb8f' : '1px dashed #1890ff')), display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Space>
                    {isStepResultRef ? <ShareAltOutlined style={{ color: '#722ed1', fontSize: 16 }} /> : (isResultOnly ? <FunctionOutlined style={{ color: '#52c41a', fontSize: 16 }} /> : (isExternal ? <DatabaseOutlined style={{ color: '#52c41a', fontSize: 16 }} /> : <ShareAltOutlined style={{ color: '#1890ff', fontSize: 16 }} />))}
                    <Text strong>{activeLanguage === 'english' ? fieldData.enLabel : fieldData.mrLabel}</Text>
                    <Tag color={isStepResultRef ? "purple" : (isResultOnly ? "green" : (isExternal ? "green" : "blue"))}>
                      {isStepResultRef ? 'Step Result' : (isResultOnly ? resultType : (isExternal ? (activeLanguage === 'english' ? 'External' : 'बाह्य') : (activeLanguage === 'english' ? 'Shared' : 'शेअर')))}
                    </Tag>
                    {isResultOnly && (
                      <Tag color="purple" style={{ fontSize: '10px' }}>
                        Auto-calculated
                      </Tag>
                    )}
                    {isStepResultRef && (
                      <Tag color="magenta" style={{ fontSize: '10px' }}>
                        From: {fieldData.sourceStepName}
                      </Tag>
                    )}
                    <Tag color="cyan">{getOperatorSymbol(fieldData.sharedOperator || 'add')}</Tag>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {isStepResultRef 
                        ? (activeLanguage === 'english' ? `From step "${fieldData.sourceStepName}" (result only)` : `"${fieldData.sourceStepName}" पायरीमधून (फक्त निकाल)`)
                        : isResultOnly 
                          ? (activeLanguage === 'english' ? `From ${fieldData.sourceFieldType} field (result only)` : `${fieldData.sourceFieldType} फील्डमधून (फक्त निकाल)`)
                          : (isExternal ? (activeLanguage === 'english' ? 'From external field' : 'बाह्य फील्डमधून') : (activeLanguage === 'english' ? 'From source step' : 'मूळ पायरीपासून'))
                      }
                    </Text>
                  </Space>
                  <Tooltip title="Remove Field">
                    <Button type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeArithmeticField(step.id, fieldIndex)} danger disabled={disabled} />
                  </Tooltip>
                </div>
              );
            }

            const unitSymbol = getUnitDisplay(fieldData);
            const rules = buildActiveRules(fieldData);
            const isConstant = fieldData.fieldType === 'constant';
            const currentValue = fieldValues[fieldData.id] !== undefined ? fieldValues[fieldData.id] : (isConstant ? (fieldData.constantValue || 0) : 0);
            const validationError = previewErrors[fieldData.id];
            const wordsRepresentation = !isConstant ? getWordsRepresentation(currentValue) : '';
            
            return (
              <div key={fieldData.id} style={{ marginBottom: 16, padding: '12px', background: '#fff', borderRadius: '8px', border: validationError ? '1px solid #ff4d4f' : '1px solid #e8e8e8' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <Select value={fieldData.fieldType || 'userInput'} onChange={(value) => {
                      const updatedField = { ...fieldData, fieldType: value };
                      if (value === 'constant' && (fieldData.constantValue === undefined || fieldData.constantValue === null)) {
                        updatedField.constantValue = 0;
                        setFieldValues(prev => ({ ...prev, [fieldData.id]: 0 }));
                      }
                      updateArithmeticFieldComplete(step.id, fieldIndex, updatedField);
                    }} size="small" style={{ width: 110 }} disabled={disabled}>
                      <Option value="userInput"><UserOutlined /> User Input</Option>
                      <Option value="constant"><LockOutlined /> Constant</Option>
                    </Select>

                    {isConstant && (
                      <InputNumber size="small" value={fieldData.constantValue !== undefined ? fieldData.constantValue : 0}
                        onChange={(val) => {
                          const updatedField = { ...fieldData, constantValue: val || 0 };
                          updateArithmeticFieldComplete(step.id, fieldIndex, updatedField);
                          setFieldValues(prev => ({ ...prev, [fieldData.id]: val || 0 }));
                        }} style={{ width: 100 }} disabled={disabled} />
                    )}

                    <Input value={activeLanguage === 'english' ? fieldData.enLabel : fieldData.mrLabel}
                      onChange={(e) => {
                        if (activeLanguage === 'english') updateArithmeticField(step.id, fieldIndex, 'enLabel', e.target.value);
                        else updateArithmeticField(step.id, fieldIndex, 'mrLabel', e.target.value);
                      }} size="small" style={{ width: 150 }} placeholder="Label" disabled={disabled} />

                    {fieldIndex > 0 && <Tag color="blue">{getOperatorSymbol(step.combineOperator)}</Tag>}
                    {fieldData.enRequired && <span style={{ color: '#ff4d4f', fontSize: 16, fontWeight: 'bold' }}>*</span>}
                    {fieldData.enRequired && <Tag color="red" style={{ fontSize: 10 }}>Required</Tag>}
                    {rules.slice(0, 2).map((rule, ridx) => <Tag key={ridx} color={rule.color} style={{ fontSize: '9px', margin: 0 }}>{rule.text}</Tag>)}
                  </div>
                  <Space>
                    <Tooltip title="Configure Field">
                      <Button type="text" size="small" icon={<SettingOutlined />} onClick={() => editField(step.id, fieldIndex, fieldData)} disabled={disabled} />
                    </Tooltip>
                    <Tooltip title="Add this field to another step">
                      <Button type="text" size="small" icon={<ShareAltOutlined style={{ color: '#1890ff' }} />} onClick={() => openAddToStepModal(step.id, fieldIndex, fieldData)} disabled={disabled || steps.length <= 1} />
                    </Tooltip>
                    <Tooltip title="Remove Field">
                      <Button type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeArithmeticField(step.id, fieldIndex)} danger disabled={disabled} />
                    </Tooltip>
                  </Space>
                </div>

                <div style={{ marginBottom: 12, paddingLeft: 8 }}>
                  <SimpleUnitCheckboxForFormula field={fieldData} onUpdate={(updatedField) => updateArithmeticFieldComplete(step.id, fieldIndex, updatedField)} language={activeLanguage} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', paddingLeft: 8 }}>
                  {isConstant ? (
                    <div style={{ width: 180, padding: '4px 11px', background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: '6px', textAlign: 'center' }}>
                      <Space><LockOutlined style={{ color: '#faad14', fontSize: '12px' }} /><Text strong>{fieldData.constantValue !== undefined ? fieldData.constantValue : 0}</Text></Space>
                    </div>
                  ) : (
                    <Input 
                      type="text" 
                      placeholder={activeLanguage === 'english' ? (fieldData.enPlaceholder || 'Enter value') : (fieldData.mrPlaceholder || 'मूल्य प्रविष्ट करा')}
                      value={currentValue}
                      onChange={(e) => updateFieldValue(fieldData.id, e.target.value)} 
                      size="small" 
                      style={{ width: 180 }}
                      status={validationError ? 'error' : undefined}
                      disabled={disabled} 
                    />
                  )}
                  {unitSymbol && <Tag color="green" icon={<CheckCircleOutlined />}>{unitSymbol}</Tag>}
                </div>

                {!isConstant && wordsRepresentation && !validationError && currentValue !== 0 && (
                  <div style={{ marginTop: 8, padding: '6px 12px', background: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                    <Text style={{ fontSize: '11px', color: '#333' }}>
                      {wordsRepresentation}
                      {unitSymbol && <span style={{ marginLeft: 4, color: '#666' }}>({unitSymbol})</span>}
                    </Text>
                  </div>
                )}

                {validationError && (
                  <Text type="danger" style={{ fontSize: "11px", display: "block", marginTop: 8, paddingLeft: 8 }}>
                    {validationError}
                  </Text>
                )}

                {!isConstant && (
                  <ValidationPanel
                    field={fieldData}
                    onUpdate={(updatedField) => updateArithmeticFieldComplete(step.id, fieldIndex, updatedField)}
                    language={activeLanguage}
                  />
                )}
              </div>
            );
          })
        )}

        {showStepResultValue && (
          <div style={{ marginTop: 16 }}>
            {stepIsLogical ? (
              stepResult === 0 && step.falseMessage ? (
                <div style={{ 
                  padding: '8px 12px', 
                  background: '#ffebee', 
                  border: '1px solid #ffcdd2', 
                  borderRadius: '6px',
                  color: '#c62828'
                }}>
                  <CloseCircleOutlined style={{ marginRight: 8 }} />
                  <Text style={{ color: '#c62828' }}>{step.falseMessage}</Text>
                </div>
              ) : null
            ) : (
              <div style={{ padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <Input
                    value={activeLanguage === 'english' ? (step.resultLabel?.en || `${step.name} Result`) : (step.resultLabel?.mr || `${step.name} चे निकाल`)}
                    onChange={(e) => updateStepResultLabel(step.id, activeLanguage, e.target.value)}
                    size="small"
                    style={{ width: 'auto', minWidth: 150, maxWidth: 250 }}
                    placeholder={activeLanguage === 'english' ? "Result label" : "निकाल लेबल"}
                    disabled={disabled}
                  />
                  <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                    {stepDisplayValue}
                  </Tag>
                  {stepResultWords && (
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      ({stepResultWords})
                    </Text>
                  )}
                  {isMainResult && showResult && (
                    <Tag color="green" style={{ marginLeft: 'auto' }}>Final Result</Tag>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {!showStepResultValue && (
          <div style={{ marginTop: 16, padding: '8px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, textAlign: 'center' }}>
            <Text type="secondary">
              <EyeInvisibleOutlined /> {activeLanguage === 'english' ? 'Step result is hidden (calculation still happens internally)' : 'पायरीचा निकाल लपविला आहे (गणना आतून होत राहते)'}
            </Text>
          </div>
        )}
      </Card>
    );
  };

  const finalResultWords = !isLogicalCombine ? getWordsRepresentation(finalResult) : '';
  const finalDisplayValue = !isLogicalCombine ? (finalResult || 0).toFixed(2) : finalResult;

  return (
    <Card size="small" style={{ marginBottom: 12, borderRadius: "8px", border: "2px solid #722ed1", boxShadow: "0 2px 8px rgba(114, 46, 209, 0.1)" }}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Space>
            <FunctionOutlined style={{ color: "#722ed1" }} />
            <Badge count="Formula" style={{ backgroundColor: "#722ed1" }} />
            <span style={{ fontWeight: 'bold' }}>Multi-Level Formula Field</span>
            {autoTranslate && <Tag color="green" icon={<GlobalOutlined />}>Auto ON</Tag>}
            {disabled && <Tag color="orange">Read Only</Tag>}
          </Space>
          <Space size={4}>
            <Tooltip title="Preview"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setShowPreviewModal(true)} /></Tooltip>
            <Tooltip title="Copy"><Button type="text" size="small" icon={<CopyOutlined />} onClick={onCopy} disabled={disabled} /></Tooltip>
            <Tooltip title="Delete"><Button type="text" size="small" icon={<DeleteOutlined />} onClick={onDelete} danger disabled={disabled} /></Tooltip>
          </Space>
        </div>
      }
    >
      <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}><Text type="secondary" style={{ fontSize: '12px' }}>Field Label</Text></Col>
        <Col span={16}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tag color="blue">EN</Tag>
            <Input value={activeLanguage === 'english' ? field.enLabel : field.mrLabel} 
              onChange={(e) => { if (activeLanguage === 'english') updateField("enLabel", e.target.value); else updateField("mrLabel", e.target.value); }} 
              placeholder={activeLanguage === 'english' ? "Enter English label" : "मराठी लेबल प्रविष्ट करा"} 
              size="middle" allowClear style={{ width: "100%" }} disabled={disabled} />
          </div>
        </Col>
      </Row>

      {!isSingleStep && (
        <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Result Display</Text>
          </Col>
          <Col span={16}>
            <Checkbox checked={showResult} onChange={(e) => updateShowResult(e.target.checked)} disabled={disabled}>
              <Space>
                <EyeOutlined />
                {activeLanguage === 'english' ? 'Show final result in form' : 'अंतिम निकाल फॉर्ममध्ये दाखवा'}
              </Space>
            </Checkbox>
          </Col>
        </Row>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Text strong>Calculation Steps</Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addStep} disabled={disabled}>Add Step</Button>
        </div>
        {steps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, background: '#fafafa', borderRadius: 8 }}><Text type="secondary">No steps added. Click "Add Step" to start.</Text></div>
        ) : (
          <>
            {steps.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap', gap: '8px' }}>
                {steps.map((step, idx) => (
                  <React.Fragment key={step.id}>
                    <Tag color="blue">{step.name}</Tag>
                    {idx < steps.length - 1 && <Text strong style={{ fontSize: 16 }}>{getCombineSymbol()}</Text>}
                  </React.Fragment>
                ))}
                {!isSingleStep && showResult && <Tag color="green">FINAL RESULT</Tag>}
              </div>
            )}
            {steps.length > 1 && (
              <div style={{ textAlign: 'center', marginBottom: 20, padding: '12px', background: '#fafafa', borderRadius: 8 }}>
                <Text strong style={{ marginRight: 12 }}>Combine Steps with:</Text>
                <Select value={stepCombineOp} onChange={updateStepCombineOperatorGlobal} style={{ width: 220 }} size="middle" disabled={disabled}>
                  <Select.OptGroup label="No Combination">
                    <Option value="none">None (Show step results only)</Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="Arithmetic Operations">
                    <Option value="add">+ (Addition)</Option>
                    <Option value="subtract">- (Subtraction)</Option>
                    <Option value="multiply">× (Multiplication)</Option>
                    <Option value="divide">÷ (Division)</Option>
                    <Option value="percentage">% (Percentage of)</Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="Comparison Operations">
                    <Option value="equals">== (Equals)</Option>
                    <Option value="not_equals">≠ (Not Equals)</Option>
                    <Option value="greater_than">&gt; (Greater Than)</Option>
                    <Option value="less_than">&lt; (Less Than)</Option>
                    <Option value="greater_than_equal">≥ (Greater or Equal)</Option>
                    <Option value="less_than_equal">≤ (Less or Equal)</Option>
                  </Select.OptGroup>
                </Select>

                {isLogicalCombine && (
                  <Button
                    style={{ marginLeft: 12 }}
                    size="small"
                    icon={<MessageOutlined />}
                    onClick={() => openFalseMessageModal(null, 'final')}
                    disabled={disabled}
                  >
                    {activeLanguage === 'english' ? 'Final False Message' : 'अंतिम फॉल्स मेसेज'}
                  </Button>
                )}
              </div>
            )}
            {steps.map((step, idx) => renderStep(step, idx))}
          </>
        )}
      </div>

      {!isSingleStep && showResult && steps.length > 1 && stepCombineOp !== 'none' && finalResult !== null && (
        <div style={{
          marginTop: 16,
          padding: '12px 20px',
          background: isLogicalCombine && finalResult === 0 ? '#fff2f0' : '#f0f5ff',
          borderRadius: 8,
          border: isLogicalCombine && finalResult === 0 ? '1px solid #ffccc7' : '1px solid #adc6ff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <Text strong style={{ fontSize: '14px' }}>
              {activeLanguage === 'english' ? finalResultLabel.en : finalResultLabel.mr}:
            </Text>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {isLogicalCombine ? (
                finalResult === 1 ? (
                  <span style={{ color: '#999' }}>—</span>
                ) : (
                  <Tag color="error" style={{ fontSize: '14px', padding: '4px 16px', margin: 0 }}>
                    <CloseCircleOutlined /> {finalFalseMessage || (activeLanguage === 'english' ? 'FALSE' : 'फॉल्स')}
                  </Tag>
                )
              ) : (
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 16px', margin: 0 }}>
                  {finalDisplayValue}
                </Tag>
              )}
            </div>
            {!isLogicalCombine && finalResultWords && finalResult !== 0 && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ({finalResultWords})
              </Text>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>{activeLanguage === 'english' ? 'Label:' : 'लेबल:'}</Text>
              <Input value={activeLanguage === 'english' ? finalResultLabel.en : finalResultLabel.mr}
                onChange={(e) => updateFinalResultLabel(activeLanguage, e.target.value)} size="small"
                style={{ width: 160, textAlign: 'center' }}
                placeholder={activeLanguage === 'english' ? "Final Result" : "अंतिम निकाल"} disabled={disabled} />
            </div>
          </div>
        </div>
      )}

      {isSingleStep && steps[0] && steps[0].showStepResult !== false && (
        <div style={{ marginTop: 16, textAlign: 'center' }} />
      )}

      <Row align="middle" gutter={16} style={{ marginTop: 16 }}>
        <Col span={8} style={{ textAlign: "right", paddingRight: 8 }}><Text type="secondary" style={{ fontSize: '12px' }}>Help Text</Text></Col>
        <Col span={16}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tag color="blue">EN</Tag>
            <Input.TextArea rows={2} value={activeLanguage === 'english' ? field.enHelpText : field.mrHelpText}
              onChange={(e) => { if (activeLanguage === 'english') updateField("enHelpText", e.target.value); else updateField("mrHelpText", e.target.value); }}
              placeholder="Enter help text" disabled={disabled} />
          </div>
        </Col>
      </Row>

      <SimpleUnitCheckboxForFormula field={field} onUpdate={(updatedField) => {
        updateField('hasUnit', updatedField.hasUnit);
        updateField('selectedUnit', updatedField.selectedUnit);
        updateField('selectedUnitObject', updatedField.selectedUnitObject);
        updateField('units', updatedField.units);
      }} language={activeLanguage} />

      <AddFieldToStepModal visible={addToStepModalVisible}
        onClose={() => { setAddToStepModalVisible(false); setSelectedFieldForSharing(null); setSelectedSourceStepId(null); setSelectedSourceFieldIndex(null); }}
        onConfirm={addSharedFieldToStep} fieldData={selectedFieldForSharing} sourceStepId={selectedSourceStepId}
        sourceFieldId={selectedFieldForSharing?.id} steps={steps} language={activeLanguage} />

      {/* NEW: Share Step Result Modal */}
      <ShareStepResultModal
        visible={shareStepResultModalVisible}
        onClose={() => { setShareStepResultModalVisible(false); setSelectedSourceStepForSharing(null); }}
        onConfirm={addSharedStepResultToStep}
        sourceStep={selectedSourceStepForSharing}
        sourceStepId={selectedSourceStepForSharing?.id}
        steps={steps}
        language={activeLanguage}
      />

      <SelectExternalNumberFieldsModal visible={showExternalFieldsModal}
        onClose={() => { setShowExternalFieldsModal(false); setCurrentStepIdForExternal(null); setCurrentStepFields([]); }}
        onSelectField={(field) => addExternalNumberFieldToStep(currentStepIdForExternal, field)}
        allFields={allFields} currentFormulaId={field.id} currentStepFields={currentStepFields} language={activeLanguage} />

      <FieldConfigModal visible={showFieldConfigModal} onClose={() => { setShowFieldConfigModal(false); setEditingField(null); }}
        onSave={updateFieldConfig} fieldData={editingField} activeLanguage={activeLanguage} />

      <Modal
        open={falseMessageModalVisible}
        onCancel={() => { setFalseMessageModalVisible(false); setCurrentFalseMessageStepId(null); setCurrentFalseMessageType(null); setCurrentArithmeticFieldId(null); }}
        title={
          <Space>
            <MessageOutlined style={{ color: "#ff4d4f" }} />
            <span>
              {currentFalseMessageType === 'final' 
                ? (activeLanguage === 'english' ? 'Custom False Message for Final Result' : 'अंतिम निकालासाठी कस्टम फॉल्स मेसेज')
                : currentFalseMessageType === 'step'
                  ? (activeLanguage === 'english' ? 'Custom False Message for Step Result' : 'पायरी निकालासाठी कस्टम फॉल्स मेसेज')
                  : (activeLanguage === 'english' ? 'Custom False Message for Comparison Field' : 'तुलना फील्डसाठी कस्टम फॉल्स मेसेज')}
            </span>
          </Space>
        }
        footer={[
          <Button key="cancel" onClick={() => setFalseMessageModalVisible(false)}>{activeLanguage === "english" ? "Cancel" : "रद्द करा"}</Button>,
          <Button key="save" type="primary" onClick={() => {
            const message = document.getElementById('falseMessageInput')?.value || '';
            if (currentFalseMessageType === 'final') {
              updateFinalFalseMessage(message);
            } else if (currentFalseMessageType === 'step' && currentFalseMessageStepId) {
              updateStepFalseMessage(currentFalseMessageStepId, message);
            } else if (currentFalseMessageType === 'arithmetic' && currentFalseMessageStepId && currentArithmeticFieldId !== null) {
              updateArithmeticFieldFalseMessage(currentFalseMessageStepId, currentArithmeticFieldId, message);
            }
            setFalseMessageModalVisible(false);
          }}>
            {activeLanguage === "english" ? "Save" : "जतन करा"}
          </Button>
        ]}
        width={500}
        centered
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={activeLanguage === "english" ? "Custom Message for FALSE Result" : "फॉल्स निकालासाठी कस्टम मेसेज"}
            description={activeLanguage === "english"
              ? "This message will be shown ONLY when the comparison result is FALSE. When TRUE, nothing will be shown."
              : "हा मेसेज फक्त तेव्हाच दिसेल जेव्हा तुलनेचा निकाल FALSE असेल. TRUE असताना काहीही दिसणार नाही."}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <div>
            <Text strong>{activeLanguage === "english" ? "Message for FALSE Result:" : "फॉल्स निकालासाठी मेसेज:"}</Text>
            <Input.TextArea
              id="falseMessageInput"
              rows={3}
              defaultValue={
                currentFalseMessageType === 'final' ? finalFalseMessage :
                currentFalseMessageType === 'step' && currentFalseMessageStepId ? (steps.find(s => s.id === currentFalseMessageStepId)?.falseMessage || '') :
                currentFalseMessageType === 'arithmetic' && currentFalseMessageStepId && currentArithmeticFieldId !== null ? 
                  (steps.find(s => s.id === currentFalseMessageStepId)?.arithmeticFields?.[currentArithmeticFieldId]?.falseMessage || '') : ''
              }
              placeholder={activeLanguage === "english" 
                ? "Enter message to show when result is FALSE (e.g., 'Invalid', 'Not Match', 'Error')" 
                : "निकाल FALSE असताना दाखवायचा मेसेज प्रविष्ट करा (उदा., 'अवैध', 'जुळत नाही', 'त्रुटी')"}
              style={{ marginTop: 8 }}
            />
          </div>

          <Divider />
          <div style={{ padding: "8px", background: "#fff2f0", borderRadius: "6px", border: "1px solid #ffccc7" }}>
            <Text type="secondary" style={{ fontSize: "11px" }}>
              <InfoCircleOutlined style={{ marginRight: "4px", color: "#ff4d4f" }} />
              {activeLanguage === "english"
                ? "When comparison is TRUE → Shows NOTHING (blank) | When comparison is FALSE → Shows this custom message"
                : "जेव्हा तुलना TRUE असेल → काहीही दिसणार नाही | जेव्हा तुलना FALSE असेल → हा कस्टम मेसेज दिसेल"}
            </Text>
          </div>
        </div>
      </Modal>

      <Modal 
        open={showPreviewModal} 
        onCancel={() => setShowPreviewModal(false)} 
        title={`Formula Preview - ${field.enLabel || 'Formula Field'}`}
        footer={[<Button key="close" type="primary" onClick={() => setShowPreviewModal(false)}>Close</Button>]} 
        width={700} 
        centered
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <div style={{ padding: '16px' }}>
          {steps.filter(step => step.showStepResult !== false).map((step, stepIdx) => {
            const stepResult = stepResults[step.resultVariable] || 0;
            const stepIsLogical = isLogicalOperator(step.combineOperator);
            const visibleFields = (step.arithmeticFields || []).filter(f => !f.isSharedReference || f.isResultOnly);
            
            return (
              <Card 
                key={step.id} 
                size="small" 
                style={{ marginBottom: 16, background: '#fafafa', border: '1px solid #d9d9d9' }}
                title={
                  <Space>
                    <Badge count={stepIdx + 1} style={{ backgroundColor: '#1890ff' }} />
                    <Text strong>{step.name}</Text>
                  </Space>
                }
              >
                {visibleFields.length === 0 ? (
                  <div style={{ padding: 0 }} />
                ) : (
                  visibleFields.map((fieldData) => {
                    const unitSymbol = getUnitDisplay(fieldData);
                    const isConstant = fieldData.fieldType === 'constant';
                    const isResultOnly = fieldData.isResultOnly === true;
                    const isStepResultRef = fieldData.isStepResultReference === true;
                    const currentValue = fieldValues[fieldData.id] !== undefined ? fieldValues[fieldData.id] : (isConstant ? (fieldData.constantValue || 0) : 0);
                    
                    return (
                      <div key={fieldData.id} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px', background: '#fff', borderRadius: 6, border: '1px solid #e8e8e8' }}>
                        <Text style={{ minWidth: 120 }}>{fieldData.enLabel}{fieldData.enRequired && <span style={{ color: '#ff4d4f' }}>*</span>}</Text>
                        {isConstant && <Tag color="purple">Constant: {fieldData.constantValue}</Tag>}
                        {isResultOnly && <Tag color="green">Auto-calculated</Tag>}
                        {isStepResultRef && <Tag color="magenta">Step Result</Tag>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {isConstant ? (
                            <div style={{ width: 120, padding: '4px 8px', background: '#f5f5f5', borderRadius: 4, textAlign: 'center' }}>
                              <Text strong>{fieldData.constantValue !== undefined ? fieldData.constantValue : 0}</Text>
                            </div>
                          ) : isResultOnly || isStepResultRef ? (
                            <div style={{ width: 120, padding: '4px 8px', background: '#f0fdf4', borderRadius: 4, textAlign: 'center' }}>
                              <Text type="secondary">Auto</Text>
                            </div>
                          ) : (
                            <Input
                              type="number"
                              value={currentValue}
                              onChange={(e) => updateFieldValue(fieldData.id, e.target.value)}
                              style={{ width: 120 }}
                              size="small"
                            />
                          )}
                          {unitSymbol && <Tag color="blue">{unitSymbol}</Tag>}
                        </div>
                      </div>
                    );
                  })
                )}

                <div style={{ marginTop: 12, padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Space>
                    <Text>{step.resultLabel?.[activeLanguage === 'english' ? 'en' : 'mr'] || `${step.name} Result`}:</Text>
                  </Space>
                  <div style={{ padding: '4px 11px', background: stepIsLogical && stepResult === 0 ? '#fff2f0' : '#f5f5f5', border: stepIsLogical && stepResult === 0 ? '1px solid #ffccc7' : '1px solid #d9d9d9', borderRadius: '6px', minWidth: '100px', textAlign: 'center' }}>
                    {stepIsLogical ? (
                      stepResult === 0 && step.falseMessage ? step.falseMessage : (stepResult === 0 ? 'FALSE' : '')
                    ) : (
                      (stepResult || 0).toFixed(2)
                    )}
                  </div>
                </div>
              </Card>
            );
          })}

          {!isSingleStep && stepCombineOp !== 'none' && showResult && finalResult !== null && steps.filter(s => s.showStepResult !== false).length > 0 && (
            <Card size="small" style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', padding: '12px' }}>
                <Text strong style={{ fontSize: '14px' }}>
                  {activeLanguage === 'english' ? finalResultLabel.en : finalResultLabel.mr}:
                </Text>
                <div style={{ padding: '4px 11px', background: isLogicalCombine && finalResult === 0 ? '#fff2f0' : '#f5f5f5', border: isLogicalCombine && finalResult === 0 ? '1px solid #ffccc7' : '1px solid #d9d9d9', borderRadius: '6px', minWidth: '100px', textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                  {isLogicalCombine ? (
                    finalResult === 0 ? (finalFalseMessage || 'FALSE') : ''
                  ) : (
                    (finalResult || 0).toFixed(2)
                  )}
                </div>
                {!isLogicalCombine && finalResultWords && finalResult !== 0 && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ({finalResultWords})
                  </Text>
                )}
              </div>
            </Card>
          )}
        </div>
      </Modal>
    </Card>
  );
};

// Field Configuration Modal
const FieldConfigModal = ({ visible, onClose, onSave, fieldData, activeLanguage }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (fieldData && visible) {
      const isIntegerOnly = fieldData.validation?.integerOnly === true;
      form.setFieldsValue({
        enRequired: fieldData.enRequired || false,
        allowDecimals: !isIntegerOnly && fieldData.validation?.allowDecimals !== false,
        decimalPlaces: fieldData.validation?.decimalPlaces || 2,
        allowNegative: fieldData.validation?.allowNegative || false,
        allowZero: fieldData.validation?.allowZero !== false,
        exactLength: fieldData.validation?.exactLength || null,
        integerOnly: isIntegerOnly
      });
    }
  }, [fieldData, visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const isIntegerOnly = values.integerOnly;
      
      onSave({
        ...fieldData,
        enRequired: values.enRequired,
        validation: {
          allowDecimals: isIntegerOnly ? false : values.allowDecimals,
          decimalPlaces: isIntegerOnly ? null : values.decimalPlaces,
          allowNegative: values.allowNegative,
          allowZero: values.allowZero,
          exactLength: values.exactLength,
          integerOnly: isIntegerOnly
        }
      });
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal open={visible} onCancel={onClose} title="Validation Settings" footer={[
      <Button key="cancel" onClick={onClose}>Cancel</Button>,
      <Button key="save" type="primary" onClick={handleSubmit}>Save Changes</Button>
    ]} width={500} centered destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="enRequired" valuePropName="checked" label="Required">
          <Checkbox>Required Field</Checkbox>
        </Form.Item>
        <Form.Item name="exactLength" label="Exact Number of Digits" extra="Field must have exactly this many digits">
          <InputNumber min={1} max={20} style={{ width: '100%' }} placeholder="No restriction" />
        </Form.Item>
        
        <Form.Item name="integerOnly" valuePropName="checked" label="Integer Only">
          <Checkbox>Integer only (no decimals allowed)</Checkbox>
        </Form.Item>
        
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.integerOnly !== curr.integerOnly}>
          {({ getFieldValue }) => {
            const isIntegerOnly = getFieldValue('integerOnly');
            return !isIntegerOnly && (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="allowDecimals" valuePropName="checked" label="Allow Decimals">
                      <Checkbox>Yes</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.allowDecimals !== curr.allowDecimals}>
                      {({ getFieldValue: getFieldValue2 }) => {
                        const allowDecimals = getFieldValue2('allowDecimals');
                        return allowDecimals ? (
                          <Form.Item name="decimalPlaces" label="Decimal Places">
                            <InputNumber min={0} max={6} style={{ width: '100%' }} />
                          </Form.Item>
                        ) : null;
                      }}
                    </Form.Item>
                  </Col>
                </Row>
              </>
            );
          }}
        </Form.Item>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="allowNegative" valuePropName="checked" label="Allow Negative">
              <Checkbox>Yes</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="allowZero" valuePropName="checked" label="Allow Zero">
              <Checkbox>Yes</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

// Select External Number/Arithmetic/Formula Fields Modal
const SelectExternalNumberFieldsModal = ({ visible, onClose, onSelectField, allFields, currentFormulaId, currentStepFields, language }) => {
  const [selectedField, setSelectedField] = useState(null);
  const [selectedFieldType, setSelectedFieldType] = useState('number');

  const externalFields = useMemo(() => {
    const currentFieldIds = currentStepFields.map(f => f.id);
    
    return allFields.filter(f => 
      (f.type === 'number' || f.type === 'arithmetic' || f.type === 'formula') && 
      f.id !== currentFormulaId &&
      !currentFieldIds.includes(f.id)
    );
  }, [allFields, currentFormulaId, currentStepFields]);

  const groupedFields = useMemo(() => {
    const groups = {
      number: [],
      arithmetic: [],
      formula: []
    };
    
    externalFields.forEach(field => {
      if (field.type === 'number') {
        groups.number.push(field);
      } else if (field.type === 'arithmetic') {
        groups.arithmetic.push(field);
      } else if (field.type === 'formula') {
        groups.formula.push(field);
      }
    });
    
    return groups;
  }, [externalFields]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'number': return <NumberOutlined style={{ color: '#52c41a' }} />;
      case 'arithmetic': return <CalculatorOutlined style={{ color: '#fa8c16' }} />;
      case 'formula': return <FunctionOutlined style={{ color: '#722ed1' }} />;
      default: return <NumberOutlined />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'number': return language === 'english' ? 'Number Field' : 'संख्या फील्ड';
      case 'arithmetic': return language === 'english' ? 'Arithmetic Field' : 'अंकगणित फील्ड';
      case 'formula': return language === 'english' ? 'Formula Field' : 'फॉर्म्युला फील्ड';
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'number': return '#52c41a';
      case 'arithmetic': return '#fa8c16';
      case 'formula': return '#722ed1';
      default: return '#1890ff';
    }
  };

  const handleConfirm = () => {
    if (selectedField) {
      onSelectField(selectedField);
      onClose();
      setSelectedField(null);
      setSelectedFieldType('number');
    } else {
      message.warning(language === "english" ? "Please select a field" : "कृपया एक फील्ड निवडा");
    }
  };

  const getUnitDisplay = (fieldData) => {
    if (fieldData?.selectedUnitObject) return fieldData.selectedUnitObject.unit_symbol;
    if (fieldData?.units?.enabled && fieldData.units.selectedUnitSymbol) return fieldData.units.selectedUnitSymbol;
    return null;
  };

  const getFieldPreview = (field) => {
    if (field.type === 'number') {
      const unitSymbol = getUnitDisplay(field);
      return unitSymbol ? ` (${unitSymbol})` : '';
    } else if (field.type === 'arithmetic') {
      const operator = field.arithmeticOperator || 'add';
      const fieldCount = field.arithmeticFields?.length || 0;
      return ` (${operator} - ${fieldCount} fields) → Result only`;
    } else if (field.type === 'formula') {
      const stepCount = field.formulaSteps?.length || 0;
      return ` (${stepCount} steps) → Result only`;
    }
    return '';
  };

  return (
    <Modal
      open={visible}
      onCancel={() => { onClose(); setSelectedField(null); setSelectedFieldType('number'); }}
      title={
        <Space>
          <DatabaseOutlined style={{ color: "#52c41a" }} />
          <span>{language === "english" ? "Select External Field" : "बाह्य फील्ड निवडा"}</span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={() => { onClose(); setSelectedField(null); setSelectedFieldType('number'); }}>
          {language === "english" ? "Cancel" : "रद्द करा"}
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm} disabled={!selectedField}>
          {language === "english" ? "Add to Formula" : "फॉर्म्युलामध्ये जोडा"}
        </Button>
      ]}
      width={650}
      centered
      destroyOnClose
    >
      {externalFields.length === 0 ? (
        <Alert
          message={language === "english" ? "No external fields available" : "कोणतेही बाह्य फील्ड उपलब्ध नाहीत"}
          description={language === "english" 
            ? "Please add Number, Arithmetic, or Formula fields to your form outside this formula field first" 
            : "कृपया प्रथम तुमच्या फॉर्ममध्ये या फॉर्म्युला फील्डच्या बाहेर Number, Arithmetic किंवा Formula फील्ड जोडा"}
          type="info"
          showIcon
        />
      ) : (
        <>
          <div style={{ marginBottom: 16, display: 'flex', gap: '8px', borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
            <Button
              size="small"
              type={selectedFieldType === 'number' ? 'primary' : 'default'}
              onClick={() => setSelectedFieldType('number')}
              icon={<NumberOutlined />}
            >
              Number ({groupedFields.number.length})
            </Button>
            <Button
              size="small"
              type={selectedFieldType === 'arithmetic' ? 'primary' : 'default'}
              onClick={() => setSelectedFieldType('arithmetic')}
              icon={<CalculatorOutlined />}
            >
              Arithmetic ({groupedFields.arithmetic.length})
            </Button>
            <Button
              size="small"
              type={selectedFieldType === 'formula' ? 'primary' : 'default'}
              onClick={() => setSelectedFieldType('formula')}
              icon={<FunctionOutlined />}
            >
              Formula ({groupedFields.formula.length})
            </Button>
          </div>

          {(selectedFieldType === 'arithmetic' || selectedFieldType === 'formula') && (
            <Alert
              message={language === "english" 
                ? `When adding ${selectedFieldType} fields, only the final result value will be included`
                : `${selectedFieldType} फील्ड जोडताना, फक्त अंतिम निकाल मूल्य समाविष्ट केले जाईल`}
              description={language === "english"
                ? "The field will be added as an auto-calculated value that cannot be edited directly."
                : "फील्ड एक स्वयं-गणना मूल्य म्हणून जोडले जाईल जे थेट संपादित करता येणार नाही."}
              type="info"
              showIcon
              style={{ marginBottom: 16, fontSize: '12px' }}
            />
          )}

          <Radio.Group 
            value={selectedField?.id} 
            onChange={(e) => {
              const field = externalFields.find(f => f.id === e.target.value);
              setSelectedField(field);
            }} 
            style={{ width: "100%" }}
          >
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {groupedFields[selectedFieldType].length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  <Empty 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={language === "english" 
                      ? `No ${selectedFieldType} fields available` 
                      : `${selectedFieldType} फील्ड उपलब्ध नाहीत`}
                  />
                </div>
              ) : (
                groupedFields[selectedFieldType].map(field => {
                  const unitSymbol = getUnitDisplay(field);
                  const fieldPreview = getFieldPreview(field);
                  const isSelected = selectedField?.id === field.id;
                  
                  return (
                    <div
                      key={field.id}
                      style={{
                        padding: "12px 16px",
                        marginBottom: "8px",
                        border: isSelected ? "2px solid #1890ff" : "1px solid #e8e8e8",
                        borderRadius: "8px",
                        background: isSelected ? "#e6f7ff" : "#ffffff",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onClick={() => setSelectedField(field)}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "#1890ff";
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "#e8e8e8";
                          e.currentTarget.style.backgroundColor = "#ffffff";
                        }
                      }}
                    >
                      <Radio value={field.id} style={{ width: "100%", margin: 0 }}>
                        <div style={{ width: "100%" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {getTypeIcon(field.type)}
                              <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                                {language === "english" ? (field.enLabel || field.label) : (field.mrLabel || field.label)}
                              </span>
                              {field.enRequired && <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>}
                            </div>
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                              <Tag color={getTypeColor(field.type)} style={{ fontSize: "10px", margin: 0 }}>
                                {getTypeLabel(field.type)}
                              </Tag>
                              {(field.type === 'arithmetic' || field.type === 'formula') && (
                                <Tag color="purple" style={{ fontSize: "10px", margin: 0 }}>
                                  Adds result only
                                </Tag>
                              )}
                              {field.enRequired && <Tag color="red" style={{ fontSize: "10px", margin: 0 }}>Required</Tag>}
                              {unitSymbol && <Tag color="green" style={{ fontSize: "10px", margin: 0 }}>{unitSymbol}</Tag>}
                              {fieldPreview && <Tag color="blue" style={{ fontSize: "10px", margin: 0 }}>{fieldPreview}</Tag>}
                            </div>
                          </div>
                          
                          {field.type === 'arithmetic' && field.arithmeticFields && (
                            <div style={{ marginTop: 8, fontSize: '11px', color: '#666' }}>
                              <span>Fields: </span>
                              {field.arithmeticFields.slice(0, 3).map((af, idx) => (
                                <span key={af.id}>
                                  {language === 'english' ? (af.enLabel || `Value ${idx + 1}`) : (af.mrLabel || `मूल्य ${idx + 1}`)}
                                  {idx < Math.min(2, field.arithmeticFields.length - 1) && ', '}
                                </span>
                              ))}
                              {field.arithmeticFields.length > 3 && <span>...</span>}
                              <Tag color="orange" style={{ fontSize: '10px', marginLeft: 8 }}>
                                Result: {field.arithmeticOperator || 'add'} calculation
                              </Tag>
                            </div>
                          )}
                          
                          {field.type === 'formula' && field.formulaSteps && (
                            <div style={{ marginTop: 8, fontSize: '11px', color: '#666' }}>
                              <span>Steps: </span>
                              {field.formulaSteps.slice(0, 3).map((step, idx) => (
                                <span key={step.id}>
                                  {step.name}
                                  {idx < Math.min(2, field.formulaSteps.length - 1) && ', '}
                                </span>
                              ))}
                              {field.formulaSteps.length > 3 && <span>...</span>}
                              <Tag color="orange" style={{ fontSize: '10px', marginLeft: 8 }}>
                                Result: multi-step formula
                              </Tag>
                            </div>
                          )}
                        </div>
                      </Radio>
                    </div>
                  );
                })
              )}
            </div>
          </Radio.Group>
        </>
      )}
      
      <Divider style={{ margin: "16px 0 8px 0" }} />
      <div style={{ padding: "8px", background: "#f0f5ff", borderRadius: "6px" }}>
        <Text type="secondary" style={{ fontSize: "11px" }}>
          <InfoCircleOutlined style={{ marginRight: "4px" }} />
          {language === "english" 
            ? "Number fields are added as editable values. Arithmetic and Formula fields are added as auto-calculated read-only values (only their final result)."
            : "संख्या फील्ड संपादन करण्यायोग्य मूल्ये म्हणून जोडली जातात. अंकगणित आणि फॉर्म्युला फील्ड स्वयं-गणना केलेली फक्त-वाचा मूल्ये म्हणून जोडली जातात (फक्त त्यांचा अंतिम निकाल)."}
        </Text>
      </div>
    </Modal>
  );
};

export default MultiLevelFormulaFieldEditor;